import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { booksAr, booksEn } from './i18n-books';
import { reportsAr, reportsEn } from './i18n-reports';

/**
 * §37.1 — Arabic-first support with full RTL.
 *
 * English is the default; Arabic flips `dir` on <html>, which is what drives every
 * logical-property utility in the layout. The dictionary is flat and typed, so a
 * missing key is a compile error rather than a blank string in production.
 */

export type Locale = 'en' | 'ar';

const en = {
  // Shell
  'app.name': 'Viganium Finance',
  'app.tagline': 'Finance & payroll',
  'nav.dashboard': 'Dashboard',
  'nav.projects': 'Projects',
  'nav.tasks': 'Tasks',
  'nav.kanban': 'Kanban',
  'nav.tickets': 'Tickets',
  'nav.qTasks': 'Q Tasks',
  'nav.shifts': 'Shifts',
  'nav.mySchedule': 'My Schedule',
  'nav.departments': 'Departments',
  'nav.employees': 'Employees',
  'nav.payroll': 'Payroll',
  'nav.clients': 'Clients',
  'nav.expenses': 'Expenses',
  'nav.invoices': 'Invoices',
  'nav.payments': 'Payments',
  'nav.contracts': 'Contracts',
  'nav.dataMigration': 'Data migration',
  'nav.groupClients': 'Commercial',
  'nav.budgets': 'Budgets',
  'nav.violations': 'Violations & Penalties',
  'nav.reports': 'Reports',
  'nav.settings': 'Settings',
  'nav.auditLog': 'Audit Log',
  'nav.groupWork': 'Work',
  'nav.groupWorkforce': 'Workforce',
  'nav.groupFinance': 'Finance',
  'nav.groupAdmin': 'Administration',

  // Common
  'common.search': 'Search',
  'common.searchPlaceholder': 'Search clients, invoices, payments, contracts…',
  'common.filters': 'Filters',
  'common.clear': 'Clear',
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.create': 'Create',
  'common.edit': 'Edit',
  'common.delete': 'Delete',
  'common.close': 'Close',
  'theme.toLight': 'Switch to light theme',
  'theme.toDark': 'Switch to dark theme',
  'common.confirm': 'Confirm',
  'common.apply': 'Apply',
  'common.loading': 'Loading…',
  'common.noResults': 'Nothing to show',
  'common.notFoundHint': 'This page does not exist, or it is not yours to see.',
  'common.noResultsHint': 'Try widening the filters.',
  'common.error': 'Something went wrong',
  'common.retry': 'Retry',
  'common.all': 'All',
  'common.none': 'None',
  'common.yes': 'Yes',
  'common.no': 'No',
  'common.actions': 'Actions',
  'common.status': 'Status',
  'common.type': 'Type',
  'common.priority': 'Priority',
  'common.department': 'Department',
  'common.project': 'Project',
  'common.assignee': 'Assignee',
  'common.requester': 'Requester',
  'common.createdAt': 'Created',
  'common.updatedAt': 'Updated',
  'common.dueDate': 'Due',
  'common.amount': 'Amount',
  'common.currency': 'Currency',
  'common.date': 'Date',
  'common.description': 'Description',
  'common.notes': 'Notes',
  'common.reason': 'Reason',
  'common.total': 'Total',
  'common.name': 'Name',
  'common.code': 'Code',
  'common.email': 'Email',
  'common.phone': 'Phone',
  'common.role': 'Role',
  'common.of': 'of',
  'common.page': 'Page',
  'common.previous': 'Previous',
  'common.next': 'Next',
  'common.general': 'General',
  'common.unassigned': 'Unassigned',
  'common.required': 'Required',
  'common.optional': 'optional',
  'common.back': 'Back',
  'common.view': 'View',
  'common.refresh': 'Refresh',
  'common.export': 'Export',
  'common.from': 'From',
  'common.to': 'To',
  'common.month': 'Month',
  'common.year': 'Year',
  'common.add': 'Add',
  'common.remove': 'Remove',
  'common.submit': 'Submit',
  'common.approve': 'Approve',
  'common.reject': 'Reject',
  'common.never': 'Never',
  'common.you': 'you',

  // Auth
  'auth.signIn': 'Sign in',
  'auth.signOut': 'Sign out',
  'auth.emailLabel': 'Work email',
  'auth.passwordLabel': 'Password',
  'auth.signingIn': 'Signing in…',
  'auth.welcome': 'Welcome back',
  'auth.subtitle': 'Sign in to the finance system.',
  'auth.forgotPassword': 'Forgot your password?',
  'auth.sessionExpired': 'Your session expired. Please sign in again.',
  'auth.myProfile': 'My profile',
  'auth.changePassword': 'Change password',
  'auth.currentPassword': 'Current password',
  'auth.newPassword': 'New password',
  'auth.changePasswordSubtitle': 'You stay signed in here. Anywhere else you are signed in will ask for the new password.',
  'auth.confirmPassword': 'New password, again',
  'auth.passwordHint': 'At least {n} characters.',
  'auth.passwordTooShort': 'The new password needs at least {n} characters.',
  'auth.passwordMismatch': 'The two new passwords are not the same.',
  'auth.currentPasswordWrong': 'That is not your current password.',
  'auth.passwordChanged': 'Your password has been changed.',
  'auth.showPassword': 'Show password',
  'auth.hidePassword': 'Hide password',
  'auth.forgotTitle': 'Reset your password',
  'auth.forgotIntro': 'Enter the email you sign in with, and we will email you a link to set a new password.',
  'auth.sendResetLink': 'Email me a link',
  'auth.resetLinkSent':
    'If {email} is the email of an account, a link to set a new password is on its way. It works once, within the hour.',
  'auth.resetLinkSentHint':
    'Nothing after a few minutes? Look in your spam folder, or ask an administrator to set a new password for you.',
  'auth.backToSignIn': 'Back to sign in',
  'auth.resetTitle': 'Set a new password',
  'auth.resetIntro':
    'It becomes your password everywhere you sign in to us, and every device signed in with the old one is signed out.',
  'auth.setNewPassword': 'Set the new password',
  'auth.passwordResetDone': 'Your new password is set. Sign in with it.',
  'auth.resetLinkInvalid': 'This link has expired or was used already. Ask for a new one.',
  'auth.requestNewLink': 'Ask for a new link',

  // Dashboard
  'dash.title': 'Dashboard',
  'dash.myTasks': 'My open tasks',
  'dash.myOverdue': 'My overdue tasks',
  'dash.myTickets': 'My open tickets',
  'dash.myProjects': 'My projects',
  'dash.currentShift': 'Current shift',
  'dash.nextShift': 'Next shift',
  'dash.notOnShift': 'Not on shift',
  'dash.myPenalties': 'My penalties this month',
  'dash.awaitingMyView': 'Awaiting my view',
  'dash.unassignedTickets': 'Unassigned in my department',
  'dash.myAssigned': 'Assigned to me',
  'dash.currentQTask': 'My current Q Task',
  'dash.availableQTasks': 'Available Q Tasks',
  'dash.slaCountdowns': 'SLA countdowns',
  'dash.teamMember': 'My queue',
  'dash.teamLeader': 'Department',
  'dash.admin': 'Company',
  'dash.finance': 'Finance',
  'dash.ticketsByStatus': 'Tickets by status',
  'dash.tasksByStatus': 'Tasks by status',
  'dash.urgent': 'Urgent',
  'dash.viewBreaches': 'View SLA breaches',
  'dash.assignmentBreaches': 'Assignment SLA breaches',
  'dash.departmentWorkload': 'Workload by employee',
  'dash.staffOnShift': 'Staff on shift now',
  'dash.qTaskQueue': 'Q Task queue',
  'dash.blockedQTasks': 'Blocked Q Tasks',
  'dash.slaCompliance': 'Assignment SLA compliance',
  'dash.activeDepartments': 'Active departments',
  'dash.activeProjects': 'Active projects',
  'dash.payrollStatus': 'Payroll status',
  'dash.monthExpenses': 'Expenses this month',
  'dash.expenseBreakdown': 'Expenses by category',
  'dash.overdueInvoices': 'Overdue invoices',
  // What's new: a tip for each feature shipped lately that the reader may open (2026-11-02)
  'tips.title': 'What’s new',
  'tips.hint': 'Added lately, and open to you.',
  'tips.open': 'Open',
  'tips.gotIt': 'Got it',
  'tips.gotItLabel': 'Got it — hide “{title}”',
  'tips.dismissAll': 'Hide all',
  'tips.showAll': 'Show all ({n})',
  'tips.showLess': 'Show less',
  'dash.budgetUtilisation': 'Budget vs actual',
  'dash.incoming': 'Incoming payments',
  'dash.outgoing': 'Outgoing payments',
  'dash.nothingPending': 'Nothing pending — you are clear.',

  // Tickets
  'tickets.title': 'Tickets',
  'tickets.new': 'New ticket',
  'tickets.number': 'Number',
  'tickets.subject': 'Subject',
  'tickets.category': 'Category',
  'tickets.targetDepartment': 'Route to department',
  'tickets.viewSla': 'View SLA',
  'tickets.assignmentSla': 'Assignment SLA',
  'tickets.slaState': 'SLA state',
  'tickets.assign': 'Assign',
  'tickets.assignToMe': 'Assign to me',
  'tickets.reassign': 'Reassign',
  'tickets.reroute': 'Reroute',
  'tickets.resolve': 'Resolve',
  'tickets.reopen': 'Reopen',
  'tickets.reply': 'Reply',
  'tickets.internalNote': 'Internal note',
  'tickets.resolution': 'Resolution',
  'tickets.timeline': 'Timeline',
  'tickets.responsibility': 'Responsibility snapshot',
  'tickets.views': 'Views',
  'tickets.conversation': 'Conversation',
  'tickets.details': 'Details',
  'tickets.eligible': 'Eligible',
  'tickets.notEligible': 'Not eligible',
  'tickets.viewedOnTime': 'Viewed on time',
  'tickets.viewedLate': 'Viewed late',
  'tickets.notViewed': 'Not viewed',
  'tickets.onShiftAtCreation': 'On shift at creation',
  'tickets.snapshotNote':
    'Taken once, when the ticket was created. Later shift or membership changes never rewrite it.',
  'tickets.lockedBy': 'is currently handling this ticket',
  'tickets.forceUnlock': 'Force unlock',
  'tickets.lock': 'Take the lock',
  'tickets.slaMet': 'SLA met',
  'tickets.slaMissed': 'SLA missed',
  'tickets.overdue': 'Overdue',
  'tickets.awaitingMyView': 'Awaiting my view',
  'tickets.mine': 'Assigned to me',
  'tickets.requestedByMe': 'Raised by me',
  'tickets.generalOnly': 'General only',
  'tickets.handover': 'Shift handover',
  'tickets.noLock': 'No one holds the lock.',
  'tickets.viewRecorded': 'Your view has been recorded.',

  // Tasks / Kanban
  'tasks.title': 'Tasks',
  'tasks.new': 'New task',
  'kanban.title': 'Kanban board',
  'kanban.myTasks': 'My tasks',
  'kanban.dropHint': 'Drag a card to another column to change its status.',
  'kanban.invalidMove': 'That move is not allowed for this task.',

  // Q Tasks
  'qtasks.title': 'Q Tasks',
  'qtasks.new': 'New Q Task',
  'qtasks.queue': 'Department queue',
  'qtasks.head': 'Active head',
  'qtasks.availableNow': 'Available now',
  'qtasks.assignedToMe': 'Assigned to me',
  'qtasks.claim': 'Assign to me',
  'qtasks.start': 'Start',
  'qtasks.complete': 'Complete',
  'qtasks.block': 'Block',
  'qtasks.unblock': 'Unblock',
  'qtasks.release': 'Release',
  'qtasks.skip': 'Skip',
  'qtasks.reorder': 'Reorder',
  'qtasks.reorderHint': 'Only queued items can be reordered — the active head stays put.',
  'qtasks.queueBlocked': 'The queue is blocked by the current head.',
  'qtasks.sequentialNote': 'Only the first item is available. The next one opens when this one closes.',
  'qtasks.position': 'Position',
  'qtasks.claimed': 'Claimed by another team member.',

  // Shifts
  'shifts.title': 'Shifts',
  'shifts.definitions': 'Shift definitions',
  'shifts.roster': 'Monthly roster',
  'shifts.generate': 'Generate roster',
  'shifts.copy': 'Copy a previous range',
  'shifts.legend': 'M Morning · N Night · C Custom · O Off',
  'shifts.crossesMidnight': 'Crosses midnight',
  'shifts.standardHours': 'Standard hours',
  'shifts.startTime': 'Start',
  'shifts.endTime': 'End',
  'shifts.onShiftNow': 'On shift now',
  'shifts.noEvening': 'Morning, Night, Custom and Off are the only shift types.',
  'shifts.mySchedule': 'My schedule',
  'shifts.weekdays': 'Weekdays',
  'shifts.overwrite': 'Overwrite existing days',
  'shifts.holidays': 'Holidays',

  // Workforce
  'employees.title': 'Employees',
  'employees.new': 'New employee',
  'employees.activate': 'Activate',
  'employees.deactivate': 'Deactivate',
  'employees.roles': 'Roles',
  'employees.payrollProfile': 'Payroll profile',
  'employees.slaOverride': 'SLA override',
  'employees.basicSalary': 'Basic salary',
  'employees.effectiveFrom': 'Effective from',
  'employees.salaryHistory': 'Salary history',
  'employees.active': 'Active',
  'employees.inactive': 'Inactive',
  'departments.title': 'Departments',
  'departments.new': 'New department',
  'departments.members': 'Members',
  'departments.leaders': 'Leaders',
  'departments.slaOverrides': 'SLA & penalty overrides',
  'departments.inheritNote': 'Leave a field empty to inherit the company default.',
  'worklogs.title': 'Work logs',

  // Payroll
  'payroll.title': 'Payroll',
  'payroll.periods': 'Periods',
  'payroll.newPeriod': 'New period',
  'payroll.calculate': 'Calculate',
  'payroll.markPaid': 'Mark paid',
  'payroll.lock': 'Lock',
  'payroll.gross': 'Gross',
  'payroll.deductions': 'Deductions',
  'payroll.net': 'Net',
  'payroll.basic': 'Basic salary',
  'payroll.overtime': 'Overtime',
  'payroll.holidayWork': 'Holiday work',
  'payroll.dayOffWork': 'Day-off work',
  'payroll.nightAllowance': 'Night shift allowance',
  'payroll.otherAllowances': 'Other allowances',
  // Every approved penalty: missed SLAs and breaks past their limit.
  'payroll.slaPenalties': 'Penalties',
  'payroll.lineItems': 'Line items',
  'payroll.employees': 'Employees',
  'payroll.adjustment': 'Adjustment',
  'payroll.myPayslips': 'My payslips',
  'payroll.lockedNote': 'This period is locked. Corrections go in an open period as adjustments.',
  // Payroll waits for every worked day of the month to be judged against its hours (2026-10-21).
  'payroll.daysWaiting': 'Not calculated: {count} worked day(s) of this month are not judged yet.',
  'payroll.daysWaitingTitle': '{count} worked day(s) not judged yet',
  'payroll.daysWaitingNote':
    'A day is judged against its hours once it is over and none of its shifts is open — and only then are its penalties final. Calculate again once these are.',
  'payroll.waiting.SHIFT_OPEN': 'A shift of it is still open — end it in Attendance',
  'payroll.waiting.END_NOT_SET': 'A forgotten shift waits for a manager to set its end',
  'payroll.waiting.DAY_NOT_OVER': 'The day is not over yet',
  'payroll.openShift': 'Open the shift',
  // Overtime of the month not settled yet (2026-10-21): what it pays is not known.
  'payroll.overtimeWaiting': 'Not calculated: {count} overtime request(s) of this month are not settled yet.',
  'payroll.overtimeWaitingTitle': '{count} overtime request(s) not settled yet',
  'payroll.overtimeWaitingNote':
    'Overtime is paid once an administrator approves it in the admin and the employee accepts it — for the time worked that day beyond its hours. Calculate again once each is settled or cancelled.',
  'payroll.overtimeWaiting.PENDING': 'Waits for an administrator’s approval',
  'payroll.overtimeWaiting.APPROVED': 'Waits for the employee’s answer',
  'payroll.openOvertime': 'Open the overtime',
  // An employee's objection to an approved penalty of the month not decided yet (2026-10-22).
  'payroll.objectionsWaiting': 'Not calculated: {count} objection(s) to this month’s penalties are not decided yet.',
  'payroll.objectionsWaitingTitle': '{count} objection(s) to penalties not decided yet',
  'payroll.objectionsWaitingNote':
    'An employee objects to an approved penalty of this month, and whether it is deducted is not known until an administrator decides: rejecting the objection keeps the penalty, approving it cancels it. Calculate again once each is decided.',
  'payroll.objectionWaits': 'Waits for an administrator’s decision',
  'payroll.objectionDays': '{days} day(s) of salary',
  'payroll.openPenalty': 'Open the penalty',
  // A day of the month more than an hour short, waiting for an administrator's decision (CR-004).
  'payroll.shortDaysWaiting': 'Not calculated: {count} day(s) of this month more than an hour short wait for an administrator’s decision.',
  'payroll.shortDaysWaitingTitle': '{count} short day(s) waiting for a decision',
  'payroll.shortDaysWaitingNote':
    'A day worked more than an hour short costs what an administrator decides — a part of a day’s pay, a day of annual leave and nothing from the salary, or nothing if excused — and nothing until then. Calculate again once each is decided in the admin.',
  'payroll.shortBy': '{time} short',
  'payroll.shortWaits': 'Waits for an administrator’s decision',
  'payroll.openShortDay': 'Open the short day',

  // Finance
  'expenses.title': 'Expenses',
  'expenses.new': 'New expense',
  'expenses.categories': 'Categories',
  'expenses.vendor': 'Vendor',
  'expenses.receipt': 'Receipt number',
  'expenses.markPaid': 'Mark paid',
  // Clients (CR-001)
  'clients.title': 'Clients',
  'clients.new': 'New client',
  'clients.company': 'Company',
  'clients.companyName': 'Company name',
  'clients.companyNameAr': 'Company name (Arabic)',
  'clients.legalName': 'Legal name',
  'clients.code': 'Client code',
  'clients.codeHint': 'Left blank, the next code is generated.',
  'clients.codeFollows': 'The last client code is {code}; this one comes next. Change it if you need to.',
  'clients.codeFirst': 'The first client code. Change it if you need to.',
  'clients.codeEditHint': 'Invoices show the client’s current code, old ones included.',
  'clients.codeTaken': 'The client code {code} belongs to another client.',
  'clients.country': 'Country',
  'clients.currency': 'Transaction currency',
  'clients.currencyHint': 'The currency you bill this client in. It is not derived from the country.',
  'clients.address': 'Address',
  'clients.taxNumber': 'Tax number',
  'clients.registrationNumber': 'Registration number',
  'clients.website': 'Website',
  'clients.logo': 'Logo',
  'clients.logoHint': 'Printed at the top of the client’s contracts, opposite ours. PNG, JPG, WebP or GIF; it is saved as a PNG.',
  'clients.logoAdd': 'Upload a logo',
  'clients.logoReplace': 'Replace',
  'clients.logoRemove': 'Remove',
  'clients.logoUploading': 'Uploading…',
  'clients.logoAlt': 'The logo of {client}',
  'clients.logoUnreadable': 'This picture could not be read. Use a PNG, JPG, WebP or GIF.',
  'clients.logoTooLarge': 'The logo is still larger than {mb} MB after it was made smaller. Use a simpler picture.',
  'clients.logoRefused': 'The logo could not be saved: {reason}',
  'clients.logoStorage': 'The file storage could not be reached. Try again; if it keeps failing, tell an administrator.',
  'clients.contacts': 'Contacts',
  'clients.newContact': 'Add contact',
  'clients.jobTitle': 'Job title',
  'clients.primaryContact': 'Primary contact',
  'clients.contactEnglishNote': 'Contacts are kept in English: letters A–Z, digits 0–9 and keyboard symbols.',
  'clients.contactEnglishOnly': 'Write this in English.',
  'clients.contactNotEnglish': 'Not in English',
  'clients.primary': 'Primary',
  'clients.overview': 'Overview',
  'clients.financialSummary': 'Financial summary',
  'clients.totalInvoiced': 'Total invoiced',
  'clients.totalPaid': 'Total paid',
  'clients.totalOutstanding': 'Outstanding',
  'clients.overdueAmount': 'Overdue',
  'clients.openInvoices': 'Open invoices',
  'clients.collectedLocal': 'Collected in base currency',
  'clients.deactivate': 'Deactivate',
  'clients.reactivate': 'Reactivate',
  'clients.deactivateConfirm':
    'Deactivating keeps every project, invoice and payment. The client simply stops appearing in pickers.',
  'clients.inactiveWarning': 'This client is inactive and cannot be invoiced.',
  'clients.noClient': 'No client — internal',
  'clients.internalProject': 'Internal',
  'projects.new': 'New project',
  'projects.ownOnlyNote': 'Only projects you own or belong to are listed.',
  'projects.addMember': 'Add member',
  'tickets.rerouteNote':
    'Rerouting records a routing event. The original responsibility snapshot and the original assignment deadline stay exactly as they were (§11.6).',
  'tickets.reassignNote': 'Reassignment is audited, so a reason is required.',
  'shifts.snapshotNote': 'Editing a day never rewrites an SLA snapshot that already exists (§15.5).',
  'shifts.weekdaysHint': 'Leave all unchecked to apply to every day in the range.',
  'shifts.midnightHint': 'An end at or before the start means the shift crosses midnight (§15.3).',
  'shifts.multiplier': 'Multiplier',
  'shifts.multiplierHint': 'Pay multiplier when an employee works this holiday (§18.3).',
  'payroll.adjustmentHint': 'Positive adds to the net; negative deducts from it.',
  'qtasks.queueTailNote':
    'A new Q Task joins the end of the queue. It becomes available only when everything ahead of it closes (§21.4).',
  'tasks.generalHint': 'Leave empty for a general task.',
  'tasks.projectMemberHint': 'Only members of this project are listed.',
  'tickets.slaStartsNote': 'The SLA clocks start the moment this ticket is created.',
  'violations.cancelHint':
    'A penalty already applied to payroll cannot be cancelled — post an adjustment instead (§13.4).',
  'violations.voidHint': 'Voiding also cancels any penalty that has not yet reached payroll (§11.6).',
  'projects.clientLocked':
    'This project already has invoices, so its client cannot be changed — the invoices were issued against this pairing.',
  'clients.statement': 'Statement',
  'clients.noneYet': 'No clients yet',
  'clients.noneYetHint': 'Every invoice belongs to a client, so start here.',

  // Invoices & payments (CR-001)
  'invoices.client': 'Client',
  'invoices.project': 'Project',
  'invoices.noProject': 'No project',
  'invoices.projectHint': 'Only this client’s projects are listed.',
  // A contract keeps its project in support while its dates cover the day (2026-10-09, 2026-10-27).
  'contracts.projectHint':
    'Only this client’s projects are listed, finished ones too: when a contract starts, its project goes into support and its end date becomes the day before.',
  'contracts.projectHintOpen':
    'When this contract starts, {project} goes into support, and its end date becomes the day before the contract’s start.',
  'contracts.projectHintSupport': '{project} is in support; this contract keeps it there while its dates cover the day.',
  'contracts.projectHintCompleted':
    '{project} is completed. When this contract starts it is in support again, so tasks and tickets can be raised on it, and its end date becomes the day before.',
  'contracts.projectHintArchived':
    '{project} is archived. Saving this contract brings it back; when the contract starts it is in support, and its end date becomes the day before.',
  'contracts.projectHintCancelled':
    '{project} is cancelled. Saving this contract brings it back; when the contract starts it is in support, and its end date becomes the day before.',
  'contracts.projectHintSupportByHand':
    '{project} was put in support by hand. Once this contract starts it is supported under the contract, and completed once no contract covers the day.',
  'invoices.pickClientFirst': 'Choose a client first',
  'invoices.items': 'Line items',
  'invoices.addItem': 'Add line',
  'invoices.itemDescription': 'Description',
  'invoices.quantity': 'Qty',
  'invoices.unitPrice': 'Unit price',
  'invoices.lineTotal': 'Line total',
  'invoices.discount': 'Discount',
  'invoices.addDiscount': 'Add discount',
  'invoices.changeDiscount': 'Change discount',
  'invoices.discountIn': 'Discount in {currency}',
  'invoices.discountHint': '{share} of the subtotal. In {currency}, the invoice’s currency — its payments are in it too.',
  'invoices.discountReason': 'Reason',
  'invoices.discountReasonHint': 'Optional. Kept in the audit log.',
  'invoices.discountNegative': 'The discount cannot be negative.',
  'invoices.discountOverSubtotal': 'The discount cannot be larger than the subtotal, {amount}.',
  'invoices.discountBelowPaid': '{paid} is paid already, so the discount can be at most {max}.',
  'invoices.discountWholeInvoice': 'That leaves nothing to pay. To waive the whole invoice, cancel it instead.',
  'invoices.discountCancelled': 'A cancelled invoice cannot be discounted.',
  'invoices.totalWas': 'was {amount}',
  'invoices.issue': 'Issue invoice',
  'invoices.cancel': 'Cancel invoice',
  'invoices.cancelReason': 'Why is this invoice being cancelled?',
  'invoices.collectedLocal': 'Actually collected',
  'invoices.owedToday': 'Still owed',
  'invoices.currencyDiffers': 'This client is normally billed in {currency}.',
  'invoices.clientCurrency': 'Client bills in',
  'invoices.currencyOverridden': 'differs',
  'invoices.invoiceCurrency': 'Invoiced in',
  'invoices.rateOnPayment': 'Entered with each payment, at the rate to {base} on the day the money arrives.',
  'invoices.ratePerPayment':
    'Each payment keeps the rate it was recorded at. What is still owed is valued at today’s rate in ' +
    'Settings › Currencies until it is paid.',
  'invoices.noRateToday': 'No rate for {code} in Settings › Currencies yet.',
  'invoices.draftNotice': 'This invoice is a draft. Issue it before recording a payment.',
  'invoices.outstandingOnly': 'With a balance owing',
  'invoices.byCurrency': 'By currency',
  'invoices.downloadPdf': 'Download PDF',

  'payments.receipt': 'Receipt',
  'payments.number': 'Payment number',
  'payments.exchangeRate': 'Exchange rate',
  'payments.exchangeRateSource': 'Rate source',
  'payments.localAmount': 'Amount in base currency',
  'payments.rateHint':
    'The rate that applied on the payment date. It is stored with the payment and never recalculated.',
  'payments.rateOn': 'Rate to {base} on {date}',
  'payments.rateOnHint': 'The rate on the day the money arrived. Saved with this payment, so a later rate change never moves it.',
  'payments.transferReceipt': 'Transfer receipt',
  'payments.transferReceiptAdd': 'Attach receipt',
  'payments.transferReceiptDrop': 'or drop a PDF or a picture here',
  'payments.transferReceiptDropHere': 'Drop the receipt here',
  'payments.transferReceiptUploading': 'Uploading…',
  'payments.transferReceiptOpen': 'Open the transfer receipt',
  'payments.transferReceiptReplace': 'Replace',
  'payments.transferReceiptRemove': 'Remove',
  'payments.transferReceiptHint': 'Optional. A PDF or a picture (PNG, JPG, WebP or GIF) of up to {mb} MB.',
  'payments.transferReceiptHintPlain': 'Optional. A PDF or a picture (PNG, JPG, WebP or GIF).',
  'payments.transferReceiptTooLarge': 'The file is larger than {mb} MB.',
  'payments.transferReceiptWrongType': 'Attach a PDF or a picture: PNG, JPG, WebP or GIF.',
  'payments.transferReceiptStorage': 'The file storage could not be reached. Try again; if it keeps failing, tell an administrator.',
  'payments.transferReceiptFailed': 'The receipt could not be uploaded: {reason}',
  'payments.transferReceiptRefused': 'The payment was not saved — the transfer receipt was refused: {reason}',
  'payments.reverse': 'Reverse payment',
  'payments.reverseReason': 'Why is this payment being reversed?',
  'payments.reversed': 'Reversed',
  'payments.includeReversed': 'Include reversed',
  'payments.alreadyPaid': 'Already paid',
  'payments.invoiceTotal': 'Invoice total',
  'payments.collectedTotal': 'Collected in base currency',

  // Data migration (CR-001 §50–§53)
  'migration.title': 'Data migration',
  'migration.subtitle': 'Invoices and payments that predate the client-centric restructure.',
  'migration.complete': 'Nothing left to migrate',
  'migration.completeHint': 'Every invoice has a client and every payment has an invoice.',
  'migration.unlinkedInvoices': 'Unlinked invoices',
  'migration.unlinkedPayments': 'Unlinked payments',
  'migration.reason': 'Why it is here',
  'migration.restore': 'Assign and restore',
  'migration.restoreInvoice': 'Restore invoice',
  'migration.restorePayment': 'Restore payment',
  'migration.discard': 'Discard',
  'migration.discardReason': 'Why is this row being written off instead of restored?',
  'migration.originalInvoice': 'Original invoice',
  'migration.pickInvoice': 'Which invoice does this payment settle?',
  'migration.restoreFirst': 'Restore its invoice first.',
  'migration.noFakeData':
    'Nothing is assigned automatically — an invented client would hide the problem rather than fix it.',

  // Finance reports (CR-001 §38)
  'reports.revenue': 'Revenue',
  'reports.receivables': 'Receivables',
  'reports.byClient': 'By client',
  'reports.byProject': 'By project',
  'reports.byCountry': 'By country',
  'reports.byCurrency': 'By currency',
  'reports.byPeriod': 'By period',
  'reports.aging': 'Aging',
  'reports.agingCurrent': 'Current',
  'reports.aging1_30': '1–30 days',
  'reports.aging31_60': '31–60 days',
  'reports.aging61_90': '61–90 days',
  'reports.aging90': '90+ days',
  'reports.outstandingInvoices': 'Outstanding invoices',
  'reports.daysOverdue': 'Days overdue',
  'reports.clientStatement': 'Client statement',
  'reports.balance': 'Balance',
  'reports.debit': 'Invoiced',
  'reports.credit': 'Paid',
  'reports.collections': 'Collections',
  'reports.collectionsThisMonth': 'Collected this month',
  'reports.baseCurrency': 'Base currency',
  'reports.inBaseCurrency': 'All figures in {currency}',
  'reports.billedIn': 'Billed in',
  'reports.sourceAmount': 'Original amount',
  'reports.rate': 'Rate',
  'reports.noRateWarning':
    'No rate is set for {currencies}, so what is still owed in it is left out of these totals. Add one in Settings > Currencies.',
  'reports.invoiced': 'Invoiced',
  'reports.collected': 'Collected',
  'reports.outstanding': 'Outstanding',

  'invoices.title': 'Invoices',
  'invoices.new': 'New invoice',
  // An invoice for one of a contract's billing periods, created by hand (2026-10-27).
  'invoices.newForContract': 'Invoice a contract period',
  'invoices.forContract': 'Contract',
  'invoices.contractPeriod': 'Period',
  'invoices.suggestedAmount': 'Suggested amount',
  'invoices.contractHint':
    'Filled in from the contract: the client, project and currency are the contract’s. Check the rest and change what you need — it is saved as a draft, to issue from its page.',
  'invoices.fromTheContract': 'From the contract',
  'invoices.counterparty': 'Counterparty',
  'invoices.issueDate': 'Issued',
  'invoices.dueDate': 'Due',
  'invoices.subtotal': 'Subtotal',
  'invoices.tax': 'Tax',
  'invoices.paid': 'Paid',
  'invoices.remaining': 'Remaining',
  'invoices.recordPayment': 'Record payment',
  'payments.title': 'Payments',
  'payments.new': 'Record payment',
  'payments.method': 'Method',
  'payments.reference': 'Reference',
  'budgets.title': 'Budgets',
  'budgets.new': 'New budget',
  'budgets.planned': 'Planned',
  'budgets.actual': 'Actual',
  'budgets.remaining': 'Remaining',
  'budgets.used': 'used',
  'budgets.overBudget': 'Over budget',
  'budgets.thresholdHit': 'Alert threshold reached',
  'budgets.scope': 'Scope',
  'budgets.threshold': 'Alert threshold',

  // Violations
  'violations.title': 'Violations & Penalties',
  'violations.violations': 'Violations',
  'violations.penalties': 'Penalties',
  'violations.type': 'Type',
  'violations.deadline': 'Deadline',
  'violations.actualAction': 'Actual action',
  'violations.void': 'Void',
  'violations.voidReason': 'Why is this violation being voided?',
  'violations.cancel': 'Cancel penalty',
  'violations.effectiveMonth': 'Payroll month',
  'violations.byEmployee': 'By employee',
  'violations.appliedToPayroll': 'Applied to payroll',

  // Reports
  'reports.title': 'Reports',
  'reports.operations': 'Operations',
  'reports.workforce': 'Workforce',
  'reports.finance': 'Finance',
  'reports.slaCompliance': 'SLA compliance',
  'reports.onTime': 'On time',
  'reports.late': 'Late',
  'reports.avgViewDelay': 'Average first-view delay',
  'reports.qThroughput': 'Q Task throughput',
  'reports.blockedTime': 'Average blocked time',
  'reports.overtimeByEmployee': 'Overtime by employee',
  'reports.penaltiesByEmployee': 'Penalties by employee',

  // Settings
  'settings.title': 'Settings',
  'settings.company': 'Company',
  'settings.tickets': 'Tickets',
  'settings.sla': 'SLA & Penalties',
  'settings.shifts': 'Shifts',
  'settings.payroll': 'Payroll',
  'settings.finance': 'Finance',
  'settings.security': 'Security',
  'settings.projects': 'Projects',
  'settings.jobs': 'Background jobs',
  'settings.currencies': 'Currencies',
  'settings.saved': 'Settings saved.',
  'settings.slaNote':
    'These are the company defaults. A department, and then an employee, can override them — in that order.',

  // Settings > Currencies
  'currencies.title': 'Currencies & rates',
  'currencies.note':
    'Switch off the currencies you never bill in — the rest disappear from every currency picker. ' +
    'A rate here is where a new payment’s rate starts, and what an unpaid invoice is worth today: a payment ' +
    'keeps the rate saved with it, so changing a rate here never moves money already received.',
  'currencies.currency': 'Currency',
  'currencies.inUse': 'In use',
  'currencies.rateTo': 'Rate to {base}',
  'currencies.rateHint': '1 {code} = ? {base}',
  'currencies.rateUpdated': 'Rate updated',
  'currencies.base': 'Base currency',
  'currencies.search': 'Search a code or name',
  'currencies.inUseCount': '{count} in use',
  'currencies.missingRate': 'No rate yet',
  'currencies.saved': 'Currencies saved.',
  'currencies.noneMatch': 'No currency matches that search.',
  'currencies.switchAllOff': 'Switch all off',

  // Audit
  'audit.title': 'Audit log',
  'audit.actor': 'Actor',
  'audit.action': 'Action',
  'audit.entity': 'Entity',
  'audit.occurredAt': 'When',
  'audit.changes': 'Changes',

  // Notifications
  'notifications.title': 'Notifications',
  'notifications.markAllRead': 'Mark all read',
  'notifications.empty': 'No notifications.',
  'notifications.unread': 'unread',
  'notifications.sound': 'Notification sound',
  'notifications.mute': 'Mute the notification sound',
  'notifications.unmute': 'Play a sound when a notification arrives',
  // A notification with no page to open: its reader may no longer open the record.
  'notifications.noPage': 'Nothing to open',
  // Desktop notifications (2026-10-22): the computer's notification center, through the browser's push service.
  'notifications.desktop': 'Desktop notifications',
  'notifications.desktopTurnOn': 'Show notifications on the desktop too',
  'notifications.desktopTurnOff': 'Desktop notifications are on — turn them off',
  'notifications.desktopBlocked': 'Desktop notifications are blocked in this browser',
  'notifications.desktopBlockedHint':
    'This browser blocks notifications from this site. Allow them in its site settings — the icon beside the address — then reload the page.',
  'notifications.desktopPageOnly':
    'This browser has no push service, so they show only while a page of the system is open.',
  'notifications.desktopPromptTitle': 'Get notifications on your desktop',
  'notifications.desktopPromptBody':
    'New notifications also show in your computer’s notification center — even when this page is in the background or closed, as long as the browser is running.',
  'notifications.desktopPromptOn': 'Turn on',
  'notifications.desktopPromptLater': 'Not now',
  'notifications.desktopSampleTitle': 'Desktop notifications are on',
  'notifications.desktopSampleBody': 'New notifications will show here while a page of the system is open.',

  // Sys — the two systems
  'systems.admin': 'Admin',
  'systems.sys': 'Finance',
  'nav.groupSpending': 'Spending',
  'nav.salaries': 'Salaries',
  'nav.sysAccess': 'Finance access',
  'auth.noSysAccess': 'This account has no access to Finance yet. Ask a Finance admin to give you access.',
  'audit.app': 'System',
  'audit.sysSubtitle':
    'Every change to finance, payroll, clients and access rights — who made it, when, and from which system.',

  // Sys access
  'access.title': 'Finance access',
  'access.note':
    'People are created in the admin. Here you let an employee into Finance and choose what they can do; ' +
    'their admin account and admin roles are never touched from here.',
  'access.grant': 'Give access',
  'access.grantTitle': 'Give an employee access to Finance',
  'access.editTitle': 'Change Finance roles',
  'access.revoke': 'Remove access',
  'access.revokeTitle': 'Remove access to Finance',
  'access.revokeNote':
    'Their Finance session ends at once. Their admin account, their admin roles and everything they recorded stay as they are.',
  'access.employee': 'Employee',
  'access.employeeHint': 'Active employees without Finance access. Not listed? Create them in the admin first.',
  'access.createInAdmin': 'Create an employee in the admin',
  'access.noCandidates': 'No active employee without Finance access matches.',
  'access.roles': 'Finance roles',
  'access.rolesHint': 'Pick at least one. What someone may do is everything their roles allow, together.',
  'access.permissions': '{count} permissions',
  'access.since': 'Access since',
  'access.lastSignIn': 'Last sign-in to Finance',
  'access.disabled': 'Disabled in the admin',
  'access.empty': 'Nobody has access to Finance yet.',
  'access.saved': 'Finance access saved.',
  'access.revoked': 'Access removed.',
  'access.lastAdmin': 'Nobody else could give access to Finance. Make someone else a Finance admin first.',
  'access.role.SYS_ADMIN': 'Everything in Finance, including who else gets in.',
  'access.role.ACCOUNTANT': 'Clients, contracts, invoices, payments, expenses, budgets and payroll.',
  'access.role.FINANCE_VIEWER': 'Reads clients, contracts, invoices, payments and the finance reports. Changes nothing.',

  // Salaries
  'salaries.title': 'Salaries',
  'salaries.note':
    'The salary record in force today for every active employee. Employees are created in the admin; their pay is set here.',
  'salaries.current': 'Current salary',
  'salaries.since': 'Since',
  'salaries.until': 'Until',
  'salaries.none': 'No salary record yet',
  'salaries.startsLater':
    'This salary starts on {date}. Until then there is no salary: payroll leaves this employee out, and their penalties have no money value.',
  'salaries.newRecord': 'New salary record',
  'salaries.recordNote':
    'Saving closes the previous record the day before this one starts, so nothing a past payroll used is overwritten.',
  'salaries.dailyHours': 'Daily hours',
  'salaries.dailyHoursHint':
    'Prices payroll’s hourly rate. The hours a day owes are its shift’s Standard hours, set in the admin’s Shifts.',
  'salaries.monthlyDays': 'Monthly days',
  'salaries.nightAllowancePerShift': 'Night shift allowance per shift',

  // A project's money
  'projectMoney.title': 'Project money',
  'projectMoney.openInAdmin': 'Open the project in the admin',

  // Contracts — support contracts that issue their own invoices
  'contracts.title': 'Contracts',
  'contracts.subtitle': 'Support contracts with clients. Each billing period is invoiced by hand, from the contract.',
  'contracts.new': 'New contract',
  'contracts.edit': 'Edit contract',
  'contracts.number': 'Contract no.',
  'contracts.contractTitle': 'Title',
  'contracts.titleHint': 'The invoice form starts each period’s line with it.',
  'contracts.wording': 'The contract’s text',
  'contracts.wordingHint':
    'What the contract’s PDF prints after its terms: the preamble, the clauses in order, then the conclusion. Arabic and English can sit side by side.',
  'contracts.preamble': 'Preamble',
  'contracts.preambleHint': 'Opens the contract: who the parties are and what they set out to agree.',
  'contracts.clauses': 'Clauses',
  'contracts.clausesHint': 'As many as the contract needs, numbered in this order. Each has a title, text, or both.',
  'contracts.clause': 'Clause {n}',
  'contracts.clauseTitle': 'Clause title',
  'contracts.clauseText': 'Clause text',
  'contracts.addClause': 'Add a clause',
  'contracts.removeClause': 'Remove clause {n}',
  'contracts.moveClauseUp': 'Move clause {n} up',
  'contracts.moveClauseDown': 'Move clause {n} down',
  'contracts.noClauses': 'No clauses yet.',
  'contracts.emptyClausesDropped': 'A clause with neither a title nor text is left out when you save.',
  'contracts.conclusion': 'Conclusion',
  'contracts.conclusionHint': 'Closes the contract, just before the signatures.',
  'contracts.signatures': 'Signatures',
  'contracts.signaturesHint':
    'The end of the PDF has a block for each side: the person in charge, their position, the date and the signature. A name or position written here is printed on its line; one left empty is filled in by hand.',
  'contracts.ourSide': 'First party — us',
  'contracts.clientSide': 'Second party — {client}',
  'contracts.clientSideNone': 'Second party — the client',
  'contracts.personInCharge': 'Person in charge',
  'contracts.positionOfPerson': 'Position of person in charge',
  'contracts.toFillIn': 'To fill in on paper',
  'contracts.nothingWritten': 'Nothing written yet. Edit the contract to add its preamble, clauses and conclusion.',
  'contracts.downloadPdf': 'Download PDF',
  'contracts.downloadPdfHint': 'In the language the contract is written in, with both logos and the signature blocks.',
  'contracts.startDate': 'Start date',
  'contracts.endDate': 'End date',
  'contracts.endDateHint': 'The last day the contract covers.',
  'contracts.endBeforeStart': 'The end date cannot be before the start date.',
  'contracts.runs': 'Runs',
  'contracts.titleAndClient': 'Title and client',
  'contracts.value': 'Payment value',
  'contracts.valueBasis': 'The value is for',
  'contracts.per.MONTH': 'a month',
  'contracts.per.YEAR': 'a year',
  'contracts.per.TOTAL': 'whole contract',
  'contracts.basis.MONTH': 'One month',
  'contracts.basis.YEAR': 'One year',
  'contracts.basis.TOTAL': 'The whole contract',
  'contracts.valuePer.MONTH': '{amount} a month',
  'contracts.valuePer.YEAR': '{amount} a year',
  'contracts.valuePer.TOTAL': '{amount} for the whole contract',
  'contracts.billing': 'Payment type',
  'contracts.billing.MONTHLY': 'Monthly',
  'contracts.billing.YEARLY': 'Yearly',
  'contracts.billing.ONE_TIME': 'One time',
  'contracts.billingHint.MONTHLY':
    'A billing period for every calendar month, each invoiced by hand. A part month is suggested at its days’ share.',
  'contracts.billingHint.YEARLY':
    'A billing period for every contract year, from the start date’s anniversary, each invoiced by hand. A part year is suggested at its days’ share.',
  'contracts.billingHint.ONE_TIME': 'One billing period, the whole contract: one invoice, created by hand.',
  'contracts.paymentTerms': 'Days to pay',
  'contracts.paymentTermsHint': 'The invoice form dates each of its invoices due this many days after it is issued.',
  'contracts.noRate':
    'No rate for {code} in Settings › Currencies, so what its invoices still owe has no value in {base} until one is set.',
  'contracts.status.ACTIVE': 'Active',
  'contracts.status.UPCOMING': 'Upcoming',
  'contracts.status.COMPLETED': 'Completed',
  'contracts.status.TERMINATED': 'Ended early',
  // Invoiced by hand since 2026-10-27: nothing raises a contract's invoice by itself.
  'contracts.toInvoice': 'To invoice',
  'contracts.dueCount': '{count} to invoice',
  'contracts.nextPeriod': 'Next period',
  'contracts.schedule': 'Billing periods',
  'contracts.byHandHint':
    'Each period is invoiced by hand: Create invoice opens the invoice form filled in for it, and nothing is saved until you submit it.',
  'contracts.period': 'Period',
  'contracts.createInvoice': 'Create invoice',
  'contracts.createInvoiceAhead': 'Invoice in advance',
  'contracts.cancelledOn': 'Cancelled: {numbers}',
  'contracts.paidOf': '{paid} paid of {total}',
  'contracts.state.INVOICED': 'Invoiced',
  'contracts.state.DUE': 'Not invoiced',
  'contracts.state.UPCOMING': 'Upcoming',
  'contracts.state.SKIPPED': 'Skipped',
  'contracts.state.NOT_BILLED': 'Not billed — ended early',
  'contracts.skippedHint':
    'Skipped: periods already over when the contract was entered are not invoiced through it — they are taken as billed before.',
  'contracts.previewTitle': 'The billing periods of these terms',
  'contracts.previewEmpty': 'Fill in the dates and the payment value to see its billing periods.',
  'contracts.previewInvoices': 'Periods to invoice',
  'contracts.previewTotal': 'Total to invoice',
  'contracts.issuedAtOnce':
    'Nothing is invoiced by itself: each period waits on the contract’s page until someone creates its invoice, from its first day or in advance.',
  'contracts.money': 'Money',
  'contracts.summary.total': 'Contract value',
  'contracts.summary.invoiced': 'Invoiced',
  'contracts.summary.collected': 'Collected',
  'contracts.summary.outstanding': 'Outstanding',
  'contracts.summary.notInvoiced': 'Not invoiced yet',
  'contracts.summary.upcoming': 'Periods to come',
  'contracts.summary.otherCurrency': '{count} invoice(s) in another currency are left out of these totals.',
  'contracts.payments': 'Payments',
  'contracts.paymentsHint': 'Received against its invoices — a contract is only ever paid through them.',
  'contracts.noPayments': 'No payments yet.',
  'contracts.reversed': 'Reversed',
  'contracts.newSupport': 'New support contract',
  'contracts.noneForProject':
    'No contract for this project yet. Once it is finished, a support contract covering the day lets it take tasks and tickets.',
  'contracts.terms': 'Terms',
  'contracts.entered': 'Entered on',
  'contracts.createdBy': 'Created by',
  'contracts.fromContract': 'From contract',
  'contracts.created': 'Contract created.',
  'contracts.saved': 'Contract saved.',
  'contracts.termsLocked':
    'This contract has an invoice, so its client, project, dates, payment value, payment type and currency are fixed. End it and start a new one to change them.',
  'contracts.endedLocked': 'This contract has ended, so only its title, its text and who signs it can change.',
  'contracts.clientInactive':
    'This client is inactive, so none of the contract’s periods can be invoiced until the client is reactivated.',
  'contracts.terminate': 'End contract',
  'contracts.terminateTitle': 'End this contract early',
  'contracts.terminateNote':
    'Its support stops: a finished project it covers takes no new tasks or tickets unless another contract covers it. Its invoices stay as they are, and the periods that had begun by today can still be invoiced; later ones cannot.',
  'contracts.terminateReason': 'Why is the contract ended early?',
  'contracts.terminatedOn': 'Ended early on {date} by {name}.',
  'contracts.ended': 'Contract ended.',

  // The rich-text editor
  'editor.toolbar': 'Formatting',
  'editor.bold': 'Bold',
  'editor.italic': 'Italic',
  'editor.underline': 'Underline',
  'editor.strike': 'Strikethrough',
  'editor.heading2': 'Heading',
  'editor.heading3': 'Subheading',
  'editor.bulletList': 'Bulleted list',
  'editor.orderedList': 'Numbered list',
  'editor.quote': 'Quote',
  'editor.rule': 'Divider',
  'editor.link': 'Link',
  'editor.unlink': 'Remove link',
  'editor.undo': 'Undo',
  'editor.redo': 'Redo',
  'editor.linkUrl': 'Link address',
  'editor.linkApply': 'Apply',
  'editor.linkInvalid': 'Enter a web address (https://…) or an email address.',

  // Points rewards — the cash employees redeemed wallet points for (CR-WALLET-001, 2026-10-20).
  'nav.pointsRewards': 'Points rewards',
  'payroll.pointsReward': 'Points reward',
  'payroll.dayReward': 'Day reward',
  'pointsRewards.title': 'Points rewards',
  'pointsRewards.subtitle':
    'Cash employees redeemed their wallet points for. Each is paid with the salary — the next payroll you calculate for its month or later carries it — or marked paid here once it went out another way.',
  'pointsRewards.tab.PENDING': 'To pay',
  'pointsRewards.tab.PAID': 'Paid',
  'pointsRewards.tab.CANCELLED': 'Cancelled',
  'pointsRewards.tab.all': 'All',
  'pointsRewards.status.PENDING': 'To pay',
  'pointsRewards.status.PAID': 'Paid',
  'pointsRewards.status.CANCELLED': 'Cancelled',
  'pointsRewards.pendingTotal': 'To pay in {currency}',
  'pointsRewards.pendingCount': '{n} rewards',
  'pointsRewards.pendingCountOne': '1 reward',
  'pointsRewards.search': 'Search by name or employee code',
  'pointsRewards.employee': 'Employee',
  'pointsRewards.redeemed': 'Redeemed',
  'pointsRewards.points': 'Points',
  'pointsRewards.bundles': '{n} bundles',
  'pointsRewards.bundleOne': '1 bundle',
  'pointsRewards.amount': 'Amount',
  'pointsRewards.where': 'Paid how',
  'pointsRewards.onPayroll': 'On the {month} payroll ({status})',
  'pointsRewards.notOnPayroll': 'On the next payroll calculated, from {month}',
  'pointsRewards.paidWithPayroll': 'With the {month} payroll',
  'pointsRewards.paidByHand': 'Outside payroll',
  'pointsRewards.reference': 'Ref. {ref}',
  'pointsRewards.cancelledBy': 'Cancelled by {name}',
  'pointsRewards.markPaid': 'Mark paid',
  'pointsRewards.cancel': 'Cancel',
  'pointsRewards.none': 'Nothing here.',
  'pointsRewards.paidTitle': 'Mark a points reward paid',
  'pointsRewards.paidHint': 'Only once the money went out — in cash or by transfer, outside payroll. The employee then sees it paid.',
  'pointsRewards.referenceLabel': 'Reference',
  'pointsRewards.referenceHint': 'A receipt or transfer number, if there is one.',
  'pointsRewards.paidDone': 'Marked paid.',
  'pointsRewards.cancelTitle': 'Cancel a points reward',
  'pointsRewards.cancelHint': 'Its points go back to the employee’s wallet, once. They are told it was cancelled — not why.',
  'pointsRewards.cancelOnPayroll':
    'It is on the {month} payroll, not approved yet: it comes off it, and that payslip’s net drops by {amount}.',
  'pointsRewards.reasonLabel': 'Why',
  'pointsRewards.reasonHint': 'Kept in Finance.',
  'pointsRewards.cancelSubmit': 'Cancel and return {points} points',
  'pointsRewards.cancelDone': 'Cancelled — the points went back to the wallet.',
  'pointsRewards.error.REDEMPTION_ALREADY_PROCESSED': 'This reward was paid or cancelled already, or is on a payroll that pays it.',
  'pointsRewards.error.PAYROLL_PERIOD_LOCKED': 'This reward is on an approved payroll, and is paid with it.',
  'pointsRewards.error.CONCURRENT_CHANGE': 'This reward or its payroll changed meanwhile. Look again.',
  // Finance Phase 1: partners, the historical months, the opening position (lib/i18n-books.ts).
  ...booksEn,
  // Finance Phase 2: the financial reports (lib/i18n-reports.ts).
  ...reportsEn,

  // Subscriptions (2026-10-28): the internal projects' packages, clients' subscriptions, their billing,
  // and the external systems that use Finance's API.
  'nav.groupSubscriptions': 'Subscriptions',
  'nav.subscriptionsOverview': 'Overview',
  'nav.subscriptions': 'Subscriptions',
  'nav.subscriptionPackages': 'Packages',
  'nav.subscriptionBilling': 'Billing',
  'settings.subscriptions': 'Subscription billing',
  'settings.subscriptionsNote':
    'Each subscription invoice is issued on the first day of its period by the hourly run, and falls due after the days to pay. Changing these changes no invoice already issued.',
  'settings.externalSystems': 'External systems',
  'subs.title': 'Subscriptions',
  'subs.subtitle': 'Clients’ subscriptions to the packages of the internal projects we sell — billed each month or year until cancelled.',
  'subs.new': 'New subscription',
  'subs.number': 'Number',
  'subs.client': 'Client',
  'subs.clientAndPackage': 'Client and package',
  'subs.product': 'Product',
  'subs.package': 'Package',
  'subs.cycle': 'Billing cycle',
  'subs.netPrice': 'Each period',
  'subs.before': 'before discount {amount}',
  'subs.nextBilling': 'Next invoice',
  'subs.overdueCount': '{count} invoice(s) overdue',
  'subs.system': 'External system',
  'subs.filterEnding': 'Ending at its period’s end',
  'subs.filterFailing': 'Billing failed',
  'subs.billingBy': 'Next invoice by',
  'subs.startedFrom': 'Started from',
  'subs.exportCsv': 'Export CSV',
  'subs.fromSubscription': 'From subscription',
  'subs.noneForProject': 'No subscription to this product yet.',
  'subs.projectCard': 'Sold by subscription',
  'subs.projectCardOffered': 'Offered by subscription — {count} package(s) on sale.',
  'subs.status.ACTIVE': 'Active',
  'subs.status.PAST_DUE': 'Past due',
  'subs.status.CANCELLED': 'Cancelled',
  'subs.status.UPCOMING': 'Starts {date}',
  'subs.status.ENDING': 'Ends {date}',
  'subs.cycle.MONTHLY': 'Monthly',
  'subs.cycle.YEARLY': 'Yearly',
  'subs.per.MONTHLY': 'a month',
  'subs.per.YEARLY': 'a year',
  'subs.discount.NONE': 'No discount',
  'subs.discount.PERCENT': 'Percentage',
  'subs.discount.FIXED': 'Fixed amount',
  'subs.source.FINANCE': 'Finance',
  'subs.source.API': 'An external system (API)',
  'subs.created': 'Subscription created.',
  'subs.createdInvoiced': 'Subscription created — invoice {number} issued.',
  'subs.overview.title': 'Subscriptions overview',
  'subs.overview.subtitle':
    'Contracted recurring revenue, what was invoiced and what was actually collected — three different figures, each in its own currency.',
  'subs.kpi.active': 'Active',
  'subs.kpi.pastDue': 'Past due',
  'subs.kpi.ending': 'Ending',
  'subs.kpi.endingHint': 'Set to end at their period’s end',
  'subs.kpi.cancelled': 'Cancelled',
  'subs.kpi.mrr': 'MRR',
  'subs.kpi.arr': 'ARR',
  'subs.kpi.mrrHint': 'From the running agreements’ net prices, a yearly one counted as a twelfth a month — contracted, not cash.',
  'subs.kpi.inBase': 'In {currency}, at today’s rates — contracted, not cash',
  'subs.kpi.unconverted': 'No rate today for {list}: left out of this total',
  'subs.recurringByCurrency': 'Recurring revenue by currency',
  'subs.live': 'Running',
  'subs.billingRange': 'Invoiced and collected, {from} – {to}',
  'subs.collectedBase': 'Cash collected: {amount}, at the rate of each payment',
  'subs.invoiced': 'Invoiced',
  'subs.collected': 'Collected',
  'subs.outstanding': 'Outstanding',
  'subs.invoicesCount': '{count} invoice(s)',
  'subs.paymentsCount': '{count} payment(s)',
  'subs.byProduct': 'By product',
  'subs.byPackage': 'By package',
  'subs.aging': 'What is owed, by days past due',
  'subs.agingHint': 'Subscription invoices still owing money.',
  'subs.aging.current': 'Not due yet',
  'subs.aging.d1_30': '1–30 days',
  'subs.aging.d31_60': '31–60 days',
  'subs.aging.d61_90': '61–90 days',
  'subs.aging.d90_plus': 'Over 90 days',
  'subs.nothingOwed': 'Nothing is owed.',
  'subs.trend': 'Started and cancelled, by month',
  'subs.started': 'Started',
  'subs.cancelledCount': 'Cancelled',
  'subs.upcoming': 'Invoiced in the next 30 days',
  'subs.nothingUpcoming': 'Nothing is invoiced in the next 30 days.',
  'subs.billing.title': 'Subscription billing',
  'subs.billing.subtitle':
    'The hourly run issues each period’s invoice on its first day. Nothing is invoiced ahead of its day, and no period is invoiced twice.',
  'subs.billing.run': 'Run billing now',
  'subs.billing.ran': 'Billing ran: {invoices} invoice(s), {failures} failure(s).',
  'subs.billing.busy': 'A billing run is already under way.',
  'subs.billing.autoOff':
    'Automatic billing is off in Settings › Subscription billing: nothing is invoiced until it is back on, or by hand.',
  'subs.billing.due': 'Due now',
  'subs.billing.dueHint': 'Periods that have begun and are not invoiced yet — usually empty between runs.',
  'subs.billing.failing': 'Billing failed',
  'subs.billing.failingHint': 'The period stays due and is tried again each hour once the cause is gone.',
  'subs.billing.upcoming': 'Coming in the next 14 days',
  'subs.billing.pastDue': 'Past due',
  'subs.billing.pastDueHint': 'An invoice unpaid {days} days past its due date. Nothing is cancelled for it, and billing goes on.',
  'subs.billing.runs': 'Latest runs',
  'subs.billing.trigger.SCHEDULE': 'Hourly',
  'subs.billing.trigger.MANUAL': 'By hand',
  'subs.billing.startedAt': 'Started',
  'subs.billing.examined': 'Looked at',
  'subs.billing.invoices': 'Invoices',
  'subs.billing.failures': 'Failures',
  'subs.billing.none': 'Nothing here.',
  'subs.detail.billNow': 'Bill now',
  'subs.detail.billed': 'Invoiced: {list}.',
  'subs.detail.nothingDue': 'Nothing is due: the next period begins on {date}.',
  'subs.detail.changeTerms': 'Change agreement',
  'subs.detail.cancel': 'Cancel subscription',
  'subs.detail.resume': 'Keep it running',
  'subs.detail.resumed': 'It carries on.',
  'subs.detail.billingError': 'The last billing attempt failed ({date}): {error}',
  'subs.detail.pastDue': 'Past due since {date}: an invoice is unpaid past its due date and the grace days. Billing goes on.',
  'subs.detail.ending': 'It ends on {date}, at the end of its invoiced period. Nothing after it is invoiced.',
  'subs.detail.cancelled': 'Cancelled on {date}{by}.',
  'subs.detail.invoices': 'Invoices',
  'subs.detail.invoicesHint':
    'Each period’s invoice, issued on its first day. They are ordinary invoices: paid, discounted or cancelled on their own page.',
  'subs.detail.noInvoices': 'Nothing invoiced yet.',
  'subs.detail.free': 'Nothing to pay: its periods are not invoiced.',
  'subs.detail.period': 'Period',
  'subs.detail.remaining': 'Remaining',
  'subs.detail.due': 'due {date}',
  'subs.detail.reportedBy': 'Reported by {name} — transaction {transaction}',
  'subs.detail.receipt': 'Receipt',
  'subs.detail.reinvoice': 'Invoice again',
  'subs.detail.reinvoiced': 'Invoiced again.',
  'subs.detail.versions': 'Agreement',
  'subs.detail.versionsHint': 'Every version, and the first period it applies to.',
  'subs.detail.versionShort': '#',
  'subs.detail.applies': 'Applies',
  'subs.detail.effectiveFrom': 'From {date}',
  'subs.detail.history': 'History',
  'subs.detail.agreement': 'Agreement',
  'subs.detail.listPrice': 'List price',
  'subs.detail.basePrice': 'Agreed price',
  'subs.detail.discount': 'Discount',
  'subs.detail.net': 'Each period',
  'subs.detail.startDate': 'Starts',
  'subs.detail.currentPeriod': 'Period invoiced',
  'subs.detail.nextPeriod': 'Next period',
  'subs.detail.nextPeriodValue': '{start} – {end}, invoiced on its first day',
  'subs.detail.endsOn': 'Ends on',
  'subs.detail.source': 'Made in',
  'subs.detail.createdBy': 'Made by',
  'subs.detail.money': 'Money',
  'subs.history.subscription.created': 'Made',
  'subs.history.subscription.updated': 'Changed',
  'subs.history.subscription.terms_changed': 'Agreement changed',
  'subs.history.subscription.cancelled': 'Cancelled',
  'subs.history.subscription.cancellation_scheduled': 'Set to end at its period’s end',
  'subs.history.subscription.resumed': 'Kept running',
  'subs.history.subscription.past_due': 'Past due',
  'subs.history.subscription.back_in_good_standing': 'In good standing again',
  'subs.history.subscription.billing_failed': 'Billing failed',
  'subs.history.system': 'An external system',
  'subs.history.automatic': 'Automatic',
  'subs.form.chooseClient': 'Choose a client',
  'subs.form.clientHint': 'An active client company.',
  'subs.form.chooseProduct': 'Choose a product',
  'subs.form.productHint': 'An internal project offered by subscription.',
  'subs.form.choosePackage': 'Choose a package',
  'subs.form.notSold': 'not sold',
  'subs.form.agreedPrice': 'Price of each period',
  'subs.form.priceHint': 'The package’s list price: {price}.',
  'subs.form.customPrice': 'A price of its own for this client',
  'subs.form.ownPrice': 'its own',
  'subs.form.discountPercent': 'Discount (%)',
  'subs.form.discountAmount': 'Discount ({currency})',
  'subs.form.startDate': 'Start date',
  'subs.form.startHint': 'The first day of its first period. Already behind: only the period today falls in is invoiced.',
  'subs.form.system': 'Linked to an external system',
  'subs.form.systemHint': 'That system then reads it with its invoices and receipts, and may report its payments.',
  'subs.form.externalRef': 'Their reference',
  'subs.form.externalRefHint': 'The external system’s own reference for it.',
  'subs.form.summary': 'What it comes to',
  'subs.form.choosePackageFirst': 'Choose a package to see what it comes to.',
  'subs.form.firstPeriod': 'First period',
  'subs.form.invoicedNow': 'Its first invoice is issued as soon as it is saved.',
  'subs.form.invoicedLater': 'Its first invoice is issued on {date}.',
  'subs.form.currencyNote': 'The client is billed in {client}; this package is priced in {package}.',
  'subs.form.currencyRefused': 'Billing it in another currency needs the finance.exchange_rate.edit permission.',
  'subs.form.noTax': 'No tax is added: the price is what is invoiced.',
  'subs.form.save': 'Create subscription',
  'subs.terms.title': 'Change the agreement of {number}',
  'subs.terms.hint': 'Applies from the next period not yet invoiced, which begins on {date}. What is invoiced already stays as it is.',
  'subs.terms.firstPeriod': 'First period on the new terms',
  'subs.terms.packageHint': 'Another package of the same product, in the same currency.',
  'subs.terms.cycleRestarts': 'A new cycle counts its periods from {date}.',
  'subs.terms.systemFixed': 'An external system made it, so it stays linked to that system.',
  'subs.terms.reason': 'Why',
  'subs.terms.reasonHint': 'Kept with the new version, and in the audit log.',
  'subs.terms.save': 'Save the new agreement',
  'subs.terms.saved': 'Agreement saved.',
  'subs.cancel.title': 'Cancel {number}',
  'subs.cancel.periodEnd': 'At the end of its period',
  'subs.cancel.periodEndHint': 'It runs to {date}, the last day already invoiced, then ends.',
  'subs.cancel.periodEndNone': 'There is no invoiced period to run to, so it ends today.',
  'subs.cancel.now': 'Now',
  'subs.cancel.nowHint': 'It ends today. Nothing more is invoiced.',
  'subs.cancel.invoicesStay':
    'Invoices and payments stay as they are. An invoice that should not be paid is cancelled, or given a discount, on its own page.',
  'subs.cancel.reasonHint': 'Kept on the subscription and in the audit log.',
  'subs.cancel.submit': 'Cancel subscription',
  'subs.cancel.done': 'Subscription cancelled.',
  'subs.cancel.scheduled': 'It ends on {date}.',
  'subs.cancel.openInvoices': 'Still open: {list}.',
  'pkgs.title': 'Packages',
  'pkgs.subtitle': 'The internal projects we sell by subscription, and their packages. A list price is only where a new subscription starts.',
  'pkgs.products': 'Offered projects',
  'pkgs.productsHint': 'Internal projects chosen to be sold by subscription.',
  'pkgs.noProjects': 'No internal project yet.',
  'pkgs.noProjectsHint': 'An internal project is a project with no client. Projects are made in the admin.',
  'pkgs.noneOffered': 'No project offered yet.',
  'pkgs.noneOfferedHint': 'Only the internal projects chosen to be sold by subscription are listed here.',
  'pkgs.offerProject': 'Offer a project',
  'pkgs.offerProjectHint': 'Choose an internal project to sell by subscription. It joins the list on Packages, where you add its packages.',
  'pkgs.project': 'Internal project',
  'pkgs.projectHint': 'A project with no client, made in the admin. The ones offered already, archived or cancelled are not listed.',
  'pkgs.pickProject': 'Choose a project',
  'pkgs.nothingToOffer': 'Every internal project is offered already, archived or cancelled. Internal projects are made in the admin.',
  'pkgs.notOffered': 'Not offered',
  'pkgs.offeredHint': 'Sold by subscription.',
  'pkgs.notOfferedHint': 'Not sold by subscription. Offer it to give it packages.',
  'pkgs.offer': 'Offer by subscription',
  'pkgs.stopOffering': 'Stop offering',
  'pkgs.offeredDone': 'Offered by subscription.',
  'pkgs.stoppedDone': 'No longer offered by subscription.',
  'pkgs.stillRunning': '{count} subscription(s) of it are running: cancel them first.',
  'pkgs.running': '{count} running',
  'pkgs.chooseProject': 'Choose an internal project.',
  'pkgs.showArchived': 'Show archived packages',
  'pkgs.none': 'No package yet.',
  'pkgs.noneHint': 'Give it Basic, Pro, Enterprise — each with a monthly price, a yearly one, or both.',
  'pkgs.new': 'New package',
  'pkgs.code': 'Code',
  'pkgs.codeHint': 'What external systems name it by: capitals, digits, - and _. Fixed once someone subscribes.',
  'pkgs.name': 'Name',
  'pkgs.nameAr': 'Arabic name',
  'pkgs.description': 'Description',
  'pkgs.currency': 'Currency',
  'pkgs.currencyHint': 'Its subscribers are billed in it. Fixed once someone subscribes.',
  'pkgs.monthlyPrice': 'Monthly price',
  'pkgs.yearlyPrice': 'Yearly price',
  'pkgs.priceHint': 'Leave it empty if it is not sold that way.',
  'pkgs.listPrices': 'List prices',
  'pkgs.listPriceNote': 'A list price: changing it changes no subscription, invoice or payment.',
  'pkgs.notSold': 'Not sold',
  'pkgs.onSale': 'On sale',
  'pkgs.archived': 'Archived',
  'pkgs.archive': 'Archive',
  'pkgs.restore': 'Put back on sale',
  'pkgs.archiveTitle': 'Archive {name}',
  'pkgs.archiveHint': 'No new client can subscribe to it. Its {count} running subscription(s) carry on at what they agreed.',
  'pkgs.archiveHintNone': 'No new client can subscribe to it. Nobody subscribes to it now.',
  'pkgs.archivedDone': 'Package archived.',
  'pkgs.restored': 'Package back on sale.',
  'pkgs.saved': 'Package saved.',
  'pkgs.created': 'Package created.',
  'pkgs.editTitle': 'Edit {name}',
  'pkgs.subscribers': 'Running subscriptions',
  'pkgs.noSubscribers': 'Nobody subscribes to it now.',
  'pkgs.everSubscribed': 'Subscriptions ever',
  'extsys.title': 'External systems',
  'extsys.subtitle':
    'Systems outside Viganium — a product’s own customer portal, say — that read subscriptions and invoices and report the payments they take, each with its own API tokens.',
  'extsys.new': 'Register a system',
  'extsys.none': 'No external system yet.',
  'extsys.noneHint': 'Register one, grant it projects and clients, then issue it a token.',
  'extsys.name': 'Name',
  'extsys.code': 'Code',
  'extsys.codeHint': 'Lower-case letters, digits and dashes. Made from the name when left empty.',
  'extsys.codeFixed': 'Fixed once registered.',
  'extsys.description': 'Description',
  'extsys.editTitle': 'Edit {name}',
  'extsys.saved': 'External system saved.',
  'extsys.created': 'External system registered.',
  'extsys.status.active': 'Active',
  'extsys.status.suspended': 'Suspended',
  'extsys.failedEvents': '{count} webhook(s) failed',
  'extsys.reach': 'Reaches',
  'extsys.reachValue': '{projects} project(s) · {clients} client(s) · {subscriptions} subscription(s)',
  'extsys.lastUsed': 'Last used',
  'extsys.neverUsed': 'Never used',
  'extsys.access': 'What it may reach',
  'extsys.scopes': 'Scopes',
  'extsys.scopesHint': 'The most its tokens may do. Taking one away takes it from every token at once.',
  'extsys.projects': 'Internal projects',
  'extsys.projectsHint': 'It sees nothing of any other project.',
  'extsys.noOfferedProjects': 'No internal project is offered by subscription yet.',
  'extsys.clients': 'Clients it may represent',
  'extsys.clientsHint': 'It makes subscriptions for these clients only. Subscriptions Finance links to it show to it as well.',
  'extsys.customPricing': 'May set its own prices and discounts',
  'extsys.customPricingHint': 'Off: its subscriptions take the package’s list price, with no discount.',
  'extsys.cidrs': 'Allowed addresses',
  'extsys.cidrsHint': 'One address or CIDR range a line; empty: anywhere. Defence in depth only — the token is what lets it in.',
  'extsys.anywhere': 'Anywhere',
  'extsys.subscriptionsLinked': 'Subscriptions linked',
  'extsys.reportedPayments': 'Payments reported',
  'extsys.tokens': 'API tokens',
  'extsys.tokensHint': 'Each is shown once, when it is issued. Viganium keeps only a fingerprint of it.',
  'extsys.noTokens': 'No token yet.',
  'extsys.tokenName': 'Name',
  'extsys.tokenNameHint': 'Where it is used: “Production integration”.',
  'extsys.tokenScopes': 'What it may do',
  'extsys.tokenScopesHint': 'Some of the system’s own scopes — no more than it needs.',
  'extsys.noScopes': 'The system holds no scope: give it some first.',
  'extsys.tokenExpires': 'Expires on',
  'extsys.tokenExpiresHint': 'Optional, two years ahead at most. Left empty, it does not expire.',
  'extsys.tokenIssue': 'Issue token',
  'extsys.issueToken': 'Issue a token',
  'extsys.tokenIssuedTitle': 'Token “{name}” issued',
  'extsys.token': 'Token',
  'extsys.tokenOnce':
    'Copy it now and keep it somewhere safe. It is shown this once: Viganium keeps only a fingerprint of it and cannot show it again.',
  'extsys.copy': 'Copy',
  'extsys.copied': 'Copied.',
  'extsys.copyFailed': 'It could not be copied: select it and copy it by hand.',
  'extsys.savedAck': 'I have saved it securely',
  'extsys.done': 'Done',
  'extsys.tokenStatus.ACTIVE': 'Working',
  'extsys.tokenStatus.EXPIRED': 'Expired',
  'extsys.tokenStatus.REVOKED': 'Revoked',
  'extsys.until': 'until {date}',
  'extsys.failedAttempts': '{count} failed attempt(s)',
  'extsys.rotate': 'Rotate',
  'extsys.rotateTitle': 'Rotate “{name}”',
  'extsys.rotateHint':
    'A new token takes its place, with the same name, scopes and expiry. The old one keeps working for the hours chosen — time to put the new one in place — then stops.',
  'extsys.overlap': 'Hours the old token keeps working',
  'extsys.overlapNone': 'None: it stops at once',
  'extsys.overlapHours': '{hours} hours',
  'extsys.rotateSubmit': 'Rotate token',
  'extsys.rotatedTitle': '“{name}” rotated',
  'extsys.replacedUntil': 'The old token works until {date}.',
  'extsys.revoke': 'Revoke',
  'extsys.revokeTitle': 'Revoke “{name}”',
  'extsys.revokeHint': 'It stops working at once, for good. Its history stays.',
  'extsys.revoked': 'Token revoked.',
  'extsys.suspend': 'Suspend',
  'extsys.suspendTitle': 'Suspend {name}',
  'extsys.suspendHint': 'Every call it makes is refused from now on. Its tokens and history stay, for when it is back.',
  'extsys.suspended': 'External system suspended.',
  'extsys.suspendedSince': 'Suspended since {date}: {reason}',
  'extsys.reactivate': 'Reactivate',
  'extsys.reactivated': 'External system reactivated.',
  'extsys.webhook': 'Webhook',
  'extsys.webhookHint': 'What it is told as it happens, signed. It can always read the same through the API.',
  'extsys.webhookUrl': 'Address',
  'extsys.webhookUrlHint': 'HTTPS. Each event is POSTed there as JSON, signed with X-Viganium-Signature.',
  'extsys.webhookEvents': 'Events',
  'extsys.webhookEventsHint': 'None ticked: every event.',
  'extsys.everyEvent': 'Every event',
  'extsys.webhooksEnabled': 'Send webhooks',
  'extsys.secretFirst': 'Issue a signing secret first, on the system’s page.',
  'extsys.webhookSecret': 'Signing secret',
  'extsys.secretSet': 'Set',
  'extsys.secretNotSet': 'Not set',
  'extsys.newSecret': 'New signing secret',
  'extsys.secretNew': 'The secret the system checks each delivery’s signature with. It is shown once.',
  'extsys.secretReplaces':
    'A new secret replaces the one it has at once: the system must take the new one, or it will reject what is sent.',
  'extsys.secretOnce':
    'Copy the signing secret now: it is shown this once. The system checks each delivery’s X-Viganium-Signature with it.',
  'extsys.testWebhook': 'Send a test',
  'extsys.testSent': 'A test delivery is on its way.',
  'extsys.events': 'Deliveries',
  'extsys.eventsHint': 'Sent at least once, retried with growing waits after a failure.',
  'extsys.noEvents': 'Nothing sent yet.',
  'extsys.event': 'Event',
  'extsys.event.PENDING': 'Waiting',
  'extsys.event.DELIVERED': 'Delivered',
  'extsys.event.FAILED': 'Failed',
  'extsys.attempts': 'Attempts',
  'extsys.nextAttempt': 'next try {date}',
  'extsys.redeliver': 'Send again',
  'extsys.redelivered': 'It will be sent again.',
  'extsys.history': 'History',
  'extsys.history.external_system.created': 'Registered',
  'extsys.history.external_system.updated': 'Changed',
  'extsys.history.external_system.suspended': 'Suspended',
  'extsys.history.external_system.reactivated': 'Reactivated',
  'extsys.history.external_system.token_issued': 'Token issued',
  'extsys.history.external_system.token_revoked': 'Token revoked',
  'extsys.history.external_system.token_rotated': 'Token rotated',
  'extsys.history.external_system.webhook_secret_issued': 'Signing secret issued',
  'extsys.history.external_system.webhook_tested': 'Test delivery sent',
  'extsys.history.external_system.event_redelivered': 'Delivery sent again',
  'extsys.apiBase': 'API address',
  'extsys.apiBaseHint': 'The system sends the token as Authorization: Bearer, over HTTPS only.',
  'extsys.scope.packages:read': 'Read packages',
  'extsys.scope.subscriptions:read': 'Read subscriptions',
  'extsys.scope.subscriptions:create': 'Make subscriptions',
  'extsys.scope.subscriptions:update': 'Change subscriptions',
  'extsys.scope.subscriptions:cancel': 'Cancel subscriptions',
  'extsys.scope.invoices:read': 'Read invoices',
  'extsys.scope.payments:read': 'Read payments',
  'extsys.scope.payments:report': 'Report payments',
  'extsys.scope.receipts:read': 'Fetch receipts',
} as const;

export type TranslationKey = keyof typeof en;

const ar: Record<TranslationKey, string> = {
  'app.name': 'نظام فيجانيوم المالي',
  'app.tagline': 'المالية والرواتب',
  'nav.dashboard': 'لوحة المعلومات',
  'nav.projects': 'المشاريع',
  'nav.tasks': 'المهام',
  'nav.kanban': 'لوحة كانبان',
  'nav.tickets': 'التذاكر',
  'nav.qTasks': 'مهام الطابور',
  'nav.shifts': 'الورديات',
  'nav.mySchedule': 'جدولي',
  'nav.departments': 'الأقسام',
  'nav.employees': 'الموظفون',
  'nav.payroll': 'الرواتب',
  'nav.clients': 'العملاء',
  'nav.expenses': 'المصروفات',
  'nav.invoices': 'الفواتير',
  'nav.payments': 'المدفوعات',
  'nav.contracts': 'العقود',
  'nav.dataMigration': 'ترحيل البيانات',
  'nav.groupClients': 'التجاري',
  'nav.budgets': 'الميزانيات',
  'nav.violations': 'المخالفات والجزاءات',
  'nav.reports': 'التقارير',
  'nav.settings': 'الإعدادات',
  'nav.auditLog': 'سجل التدقيق',
  'nav.groupWork': 'العمل',
  'nav.groupWorkforce': 'القوى العاملة',
  'nav.groupFinance': 'المالية',
  'nav.groupAdmin': 'الإدارة',

  'common.search': 'بحث',
  'common.searchPlaceholder': 'ابحث في العملاء والفواتير والمدفوعات والعقود…',
  'common.filters': 'عوامل التصفية',
  'common.clear': 'مسح',
  'common.save': 'حفظ',
  'common.cancel': 'إلغاء',
  'common.create': 'إنشاء',
  'common.edit': 'تعديل',
  'common.delete': 'حذف',
  'common.close': 'إغلاق',
  'theme.toLight': 'التبديل إلى الوضع الفاتح',
  'theme.toDark': 'التبديل إلى الوضع الداكن',
  'common.confirm': 'تأكيد',
  'common.apply': 'تطبيق',
  'common.loading': 'جارٍ التحميل…',
  'common.noResults': 'لا توجد بيانات',
  'common.notFoundHint': 'هذه الصفحة غير موجودة، أو لا يحق لك رؤيتها.',
  'common.noResultsHint': 'جرّب توسيع عوامل التصفية.',
  'common.error': 'حدث خطأ ما',
  'common.retry': 'إعادة المحاولة',
  'common.all': 'الكل',
  'common.none': 'لا شيء',
  'common.yes': 'نعم',
  'common.no': 'لا',
  'common.actions': 'إجراءات',
  'common.status': 'الحالة',
  'common.type': 'النوع',
  'common.priority': 'الأولوية',
  'common.department': 'القسم',
  'common.project': 'المشروع',
  'common.assignee': 'المسؤول',
  'common.requester': 'مقدّم الطلب',
  'common.createdAt': 'تاريخ الإنشاء',
  'common.updatedAt': 'آخر تحديث',
  'common.dueDate': 'تاريخ الاستحقاق',
  'common.amount': 'المبلغ',
  'common.currency': 'العملة',
  'common.date': 'التاريخ',
  'common.description': 'الوصف',
  'common.notes': 'ملاحظات',
  'common.reason': 'السبب',
  'common.total': 'الإجمالي',
  'common.name': 'الاسم',
  'common.code': 'الرمز',
  'common.email': 'البريد الإلكتروني',
  'common.phone': 'الهاتف',
  'common.role': 'الدور',
  'common.of': 'من',
  'common.page': 'صفحة',
  'common.previous': 'السابق',
  'common.next': 'التالي',
  'common.general': 'عام',
  'common.unassigned': 'غير مُسند',
  'common.required': 'مطلوب',
  'common.optional': 'اختياري',
  'common.back': 'رجوع',
  'common.view': 'عرض',
  'common.refresh': 'تحديث',
  'common.export': 'تصدير',
  'common.from': 'من',
  'common.to': 'إلى',
  'common.month': 'الشهر',
  'common.year': 'السنة',
  'common.add': 'إضافة',
  'common.remove': 'إزالة',
  'common.submit': 'إرسال',
  'common.approve': 'اعتماد',
  'common.reject': 'رفض',
  'common.never': 'أبدًا',
  'common.you': 'أنت',

  'auth.signIn': 'تسجيل الدخول',
  'auth.signOut': 'تسجيل الخروج',
  'auth.emailLabel': 'بريد العمل',
  'auth.passwordLabel': 'كلمة المرور',
  'auth.signingIn': 'جارٍ تسجيل الدخول…',
  'auth.welcome': 'أهلًا بعودتك',
  'auth.subtitle': 'سجّل الدخول إلى النظام المالي.',
  'auth.forgotPassword': 'هل نسيت كلمة المرور؟',
  'auth.sessionExpired': 'انتهت الجلسة. يُرجى تسجيل الدخول مرة أخرى.',
  'auth.myProfile': 'ملفي الشخصي',
  'auth.changePassword': 'تغيير كلمة المرور',
  'auth.currentPassword': 'كلمة المرور الحالية',
  'auth.newPassword': 'كلمة المرور الجديدة',
  'auth.changePasswordSubtitle': 'تبقى مسجّل الدخول هنا، وكل مكان آخر سجّلت الدخول فيه سيطلب كلمة المرور الجديدة.',
  'auth.confirmPassword': 'كلمة المرور الجديدة مرة أخرى',
  'auth.passwordHint': 'لا تقل عن {n} من الأحرف.',
  'auth.passwordTooShort': 'يجب ألا تقل كلمة المرور الجديدة عن {n} من الأحرف.',
  'auth.passwordMismatch': 'كلمتا المرور الجديدتان غير متطابقتين.',
  'auth.currentPasswordWrong': 'هذه ليست كلمة المرور الحالية.',
  'auth.passwordChanged': 'تم تغيير كلمة المرور.',
  'auth.showPassword': 'إظهار كلمة المرور',
  'auth.hidePassword': 'إخفاء كلمة المرور',
  'auth.forgotTitle': 'إعادة تعيين كلمة المرور',
  'auth.forgotIntro': 'أدخل البريد الذي تسجّل الدخول به، وسنرسل إليك رابطًا لتعيين كلمة مرور جديدة.',
  'auth.sendResetLink': 'أرسل لي الرابط',
  'auth.resetLinkSent': 'إن كان {email} بريدَ حسابٍ لدينا، فرابط تعيين كلمة مرور جديدة في طريقه إليك. يعمل مرة واحدة خلال ساعة.',
  'auth.resetLinkSentHint':
    'لم يصل شيء بعد دقائق؟ ابحث في مجلد الرسائل غير المرغوب فيها، أو اطلب من أحد المسؤولين تعيين كلمة مرور جديدة لك.',
  'auth.backToSignIn': 'العودة إلى تسجيل الدخول',
  'auth.resetTitle': 'تعيين كلمة مرور جديدة',
  'auth.resetIntro': 'تصبح كلمة مرورك في كل ما تسجّل الدخول إليه لدينا، ويُسجَّل خروج كل جهاز يستخدم كلمة المرور القديمة.',
  'auth.setNewPassword': 'تعيين كلمة المرور الجديدة',
  'auth.passwordResetDone': 'تم تعيين كلمة المرور الجديدة. سجّل الدخول بها.',
  'auth.resetLinkInvalid': 'انتهت صلاحية هذا الرابط أو استُخدم من قبل. اطلب رابطًا جديدًا.',
  'auth.requestNewLink': 'اطلب رابطًا جديدًا',

  'dash.title': 'لوحة المعلومات',
  'dash.myTasks': 'مهامي المفتوحة',
  'dash.myOverdue': 'مهامي المتأخرة',
  'dash.myTickets': 'تذاكري المفتوحة',
  'dash.myProjects': 'مشاريعي',
  'dash.currentShift': 'الوردية الحالية',
  'dash.nextShift': 'الوردية القادمة',
  'dash.notOnShift': 'خارج الوردية',
  'dash.myPenalties': 'جزاءاتي هذا الشهر',
  'dash.awaitingMyView': 'بانتظار اطلاعي',
  'dash.unassignedTickets': 'غير مُسندة في قسمي',
  'dash.myAssigned': 'مُسندة إليّ',
  'dash.currentQTask': 'مهمة الطابور الحالية',
  'dash.availableQTasks': 'مهام طابور متاحة',
  'dash.slaCountdowns': 'العدّ التنازلي للمهل',
  'dash.teamMember': 'طابوري',
  'dash.teamLeader': 'القسم',
  'dash.admin': 'الشركة',
  'dash.finance': 'المالية',
  'dash.ticketsByStatus': 'التذاكر حسب الحالة',
  'dash.tasksByStatus': 'المهام حسب الحالة',
  'dash.urgent': 'عاجلة',
  'dash.viewBreaches': 'تجاوزات مهلة الاطلاع',
  'dash.assignmentBreaches': 'تجاوزات مهلة الإسناد',
  'dash.departmentWorkload': 'حِمل العمل لكل موظف',
  'dash.staffOnShift': 'الموظفون في الوردية الآن',
  'dash.qTaskQueue': 'طابور المهام',
  'dash.blockedQTasks': 'مهام طابور معلّقة',
  'dash.slaCompliance': 'الالتزام بمهلة الإسناد',
  'dash.activeDepartments': 'الأقسام النشطة',
  'dash.activeProjects': 'المشاريع النشطة',
  'dash.payrollStatus': 'حالة الرواتب',
  'dash.monthExpenses': 'مصروفات هذا الشهر',
  'dash.expenseBreakdown': 'المصروفات حسب الفئة',
  'dash.overdueInvoices': 'فواتير متأخرة',
  'tips.title': 'ما الجديد',
  'tips.hint': 'أُضيف مؤخرًا، ومتاح لك.',
  'tips.open': 'افتح',
  'tips.gotIt': 'فهمت',
  'tips.gotItLabel': 'فهمت — إخفاء «{title}»',
  'tips.dismissAll': 'إخفاء الكل',
  'tips.showAll': 'عرض الكل ({n})',
  'tips.showLess': 'عرض أقل',
  'dash.budgetUtilisation': 'الميزانية مقابل الفعلي',
  'dash.incoming': 'مدفوعات واردة',
  'dash.outgoing': 'مدفوعات صادرة',
  'dash.nothingPending': 'لا يوجد شيء معلّق — كل شيء على ما يرام.',

  'tickets.title': 'التذاكر',
  'tickets.new': 'تذكرة جديدة',
  'tickets.number': 'الرقم',
  'tickets.subject': 'الموضوع',
  'tickets.category': 'الفئة',
  'tickets.targetDepartment': 'التوجيه إلى قسم',
  'tickets.viewSla': 'مهلة الاطلاع',
  'tickets.assignmentSla': 'مهلة الإسناد',
  'tickets.slaState': 'حالة المهلة',
  'tickets.assign': 'إسناد',
  'tickets.assignToMe': 'إسناد إليّ',
  'tickets.reassign': 'إعادة الإسناد',
  'tickets.reroute': 'إعادة التوجيه',
  'tickets.resolve': 'حل',
  'tickets.reopen': 'إعادة فتح',
  'tickets.reply': 'رد',
  'tickets.internalNote': 'ملاحظة داخلية',
  'tickets.resolution': 'الحل',
  'tickets.timeline': 'الخط الزمني',
  'tickets.responsibility': 'لقطة المسؤولية',
  'tickets.views': 'الاطلاعات',
  'tickets.conversation': 'المحادثة',
  'tickets.details': 'التفاصيل',
  'tickets.eligible': 'مشمول',
  'tickets.notEligible': 'غير مشمول',
  'tickets.viewedOnTime': 'اطّلع في الوقت',
  'tickets.viewedLate': 'اطّلع متأخرًا',
  'tickets.notViewed': 'لم يطّلع',
  'tickets.onShiftAtCreation': 'في الوردية عند الإنشاء',
  'tickets.snapshotNote':
    'تُؤخذ مرة واحدة عند إنشاء التذكرة. أي تغيير لاحق في الورديات أو العضوية لا يعيد كتابتها.',
  'tickets.lockedBy': 'يتعامل حاليًا مع هذه التذكرة',
  'tickets.forceUnlock': 'فك القفل إجباريًا',
  'tickets.lock': 'أخذ القفل',
  'tickets.slaMet': 'تم الالتزام بالمهلة',
  'tickets.slaMissed': 'تم تجاوز المهلة',
  'tickets.overdue': 'متأخرة',
  'tickets.awaitingMyView': 'بانتظار اطلاعي',
  'tickets.mine': 'مُسندة إليّ',
  'tickets.requestedByMe': 'أنشأتها',
  'tickets.generalOnly': 'العامة فقط',
  'tickets.handover': 'تسليم الوردية',
  'tickets.noLock': 'لا أحد يحمل القفل.',
  'tickets.viewRecorded': 'تم تسجيل اطلاعك.',

  'tasks.title': 'المهام',
  'tasks.new': 'مهمة جديدة',
  'kanban.title': 'لوحة كانبان',
  'kanban.myTasks': 'مهامي',
  'kanban.dropHint': 'اسحب البطاقة إلى عمود آخر لتغيير حالتها.',
  'kanban.invalidMove': 'هذا الانتقال غير مسموح لهذه المهمة.',

  'qtasks.title': 'مهام الطابور',
  'qtasks.new': 'مهمة طابور جديدة',
  'qtasks.queue': 'طابور القسم',
  'qtasks.head': 'المهمة النشطة',
  'qtasks.availableNow': 'متاحة الآن',
  'qtasks.assignedToMe': 'مُسندة إليّ',
  'qtasks.claim': 'إسناد إليّ',
  'qtasks.start': 'بدء',
  'qtasks.complete': 'إنهاء',
  'qtasks.block': 'تعليق',
  'qtasks.unblock': 'رفع التعليق',
  'qtasks.release': 'تحرير',
  'qtasks.skip': 'تخطّي',
  'qtasks.reorder': 'إعادة الترتيب',
  'qtasks.reorderHint': 'يمكن إعادة ترتيب العناصر في الطابور فقط — المهمة النشطة تبقى مكانها.',
  'qtasks.queueBlocked': 'الطابور متوقف بسبب المهمة النشطة المعلّقة.',
  'qtasks.sequentialNote': 'المهمة الأولى فقط متاحة. تُفتح التالية عند إغلاق الحالية.',
  'qtasks.position': 'الترتيب',
  'qtasks.claimed': 'تم إسنادها لعضو آخر بالفريق.',

  'shifts.title': 'الورديات',
  'shifts.definitions': 'تعريفات الورديات',
  'shifts.roster': 'جدول الشهر',
  'shifts.generate': 'توليد الجدول',
  'shifts.copy': 'نسخ فترة سابقة',
  'shifts.legend': 'ص صباحي · ل ليلي · خ مخصص · إ إجازة',
  'shifts.crossesMidnight': 'تمتد بعد منتصف الليل',
  'shifts.standardHours': 'الساعات المعيارية',
  'shifts.startTime': 'البداية',
  'shifts.endTime': 'النهاية',
  'shifts.onShiftNow': 'في الوردية الآن',
  'shifts.noEvening': 'الأنواع المعتمدة هي صباحي وليلي ومخصص وإجازة فقط.',
  'shifts.mySchedule': 'جدولي',
  'shifts.weekdays': 'أيام الأسبوع',
  'shifts.overwrite': 'استبدال الأيام الموجودة',
  'shifts.holidays': 'العطلات',

  'employees.title': 'الموظفون',
  'employees.new': 'موظف جديد',
  'employees.activate': 'تفعيل',
  'employees.deactivate': 'تعطيل',
  'employees.roles': 'الأدوار',
  'employees.payrollProfile': 'ملف الراتب',
  'employees.slaOverride': 'استثناء المهلة',
  'employees.basicSalary': 'الراتب الأساسي',
  'employees.effectiveFrom': 'ساري من',
  'employees.salaryHistory': 'تاريخ الرواتب',
  'employees.active': 'نشط',
  'employees.inactive': 'غير نشط',
  'departments.title': 'الأقسام',
  'departments.new': 'قسم جديد',
  'departments.members': 'الأعضاء',
  'departments.leaders': 'القادة',
  'departments.slaOverrides': 'استثناءات المهل والجزاءات',
  'departments.inheritNote': 'اترك الحقل فارغًا ليرث القيمة الافتراضية للشركة.',
  'worklogs.title': 'سجلات العمل',

  'payroll.title': 'الرواتب',
  'payroll.periods': 'الفترات',
  'payroll.newPeriod': 'فترة جديدة',
  'payroll.calculate': 'احتساب',
  'payroll.markPaid': 'تعليم كمدفوع',
  'payroll.lock': 'إقفال',
  'payroll.gross': 'الإجمالي',
  'payroll.deductions': 'الخصومات',
  'payroll.net': 'الصافي',
  'payroll.basic': 'الراتب الأساسي',
  'payroll.overtime': 'العمل الإضافي',
  'payroll.holidayWork': 'العمل في العطلات',
  'payroll.dayOffWork': 'العمل في يوم الإجازة',
  'payroll.nightAllowance': 'بدل الوردية الليلية',
  'payroll.otherAllowances': 'بدلات أخرى',
  'payroll.slaPenalties': 'الجزاءات',
  'payroll.lineItems': 'البنود',
  'payroll.employees': 'الموظفون',
  'payroll.adjustment': 'تسوية',
  'payroll.myPayslips': 'قسائم راتبي',
  'payroll.lockedNote': 'هذه الفترة مقفلة. تتم التصحيحات كتسويات في فترة مفتوحة.',
  'payroll.daysWaiting': 'لم يُحتسب: {count} من أيام عمل هذا الشهر لم يُحكم عليها بعد.',
  'payroll.daysWaitingTitle': '{count} من أيام العمل لم يُحكم عليها بعد',
  'payroll.daysWaitingNote':
    'يُحكم على اليوم مقابل ساعاته بعد انتهائه وإغلاق كل وردياته — وعندها فقط تصبح جزاءاته نهائية. احتسب مرة أخرى بعد ذلك.',
  'payroll.waiting.SHIFT_OPEN': 'إحدى وردياته ما زالت مفتوحة — أنهِها من الحضور',
  'payroll.waiting.END_NOT_SET': 'وردية منسية تنتظر مديرًا يحدد نهايتها',
  'payroll.waiting.DAY_NOT_OVER': 'لم ينتهِ اليوم بعد',
  'payroll.openShift': 'فتح الوردية',
  'payroll.overtimeWaiting': 'لم يُحتسب: {count} من طلبات العمل الإضافي لهذا الشهر لم تُحسم بعد.',
  'payroll.overtimeWaitingTitle': '{count} من طلبات العمل الإضافي لم تُحسم بعد',
  'payroll.overtimeWaitingNote':
    'يُدفع العمل الإضافي بعد موافقة مدير النظام عليه في لوحة الإدارة وقبول الموظف له — عن الوقت المعمول ذلك اليوم بعد ساعاته. احتسب مرة أخرى بعد حسم كل طلب أو إلغائه.',
  'payroll.overtimeWaiting.PENDING': 'بانتظار موافقة مدير النظام',
  'payroll.overtimeWaiting.APPROVED': 'بانتظار رد الموظف',
  'payroll.openOvertime': 'فتح العمل الإضافي',
  'payroll.objectionsWaiting': 'لم يُحتسب: {count} من الاعتراضات على جزاءات هذا الشهر لم يُبتّ فيها بعد.',
  'payroll.objectionsWaitingTitle': '{count} من الاعتراضات على الجزاءات لم يُبتّ فيها بعد',
  'payroll.objectionsWaitingNote':
    'اعترض موظف على جزاء معتمد في هذا الشهر، ولا يُعرف إن كان سيُخصم حتى يبتّ مدير النظام فيه: رفض الاعتراض يُبقي الجزاء، وقبوله يلغيه. احتسب مرة أخرى بعد البتّ في كل اعتراض.',
  'payroll.objectionWaits': 'بانتظار قرار مدير النظام',
  'payroll.objectionDays': '{days} يوم من الراتب',
  'payroll.openPenalty': 'فتح الجزاء',
  'payroll.shortDaysWaiting': 'لم يُحتسب: {count} من أيام هذا الشهر الناقصة بأكثر من ساعة بانتظار قرار الإدارة.',
  'payroll.shortDaysWaitingTitle': '{count} من الأيام الناقصة بانتظار القرار',
  'payroll.shortDaysWaitingNote':
    'اليوم الذي نقص عمله بأكثر من ساعة تقرر الإدارة كلفته — جزء من أجر يوم، أو يوم من الإجازة السنوية ولا شيء من الراتب، أو لا شيء إن أُعفي منه — ولا شيء حتى ذلك الحين. احتسب مرة أخرى بعد القرار في كل يوم من لوحة الإدارة.',
  'payroll.shortBy': 'ناقص {time}',
  'payroll.shortWaits': 'بانتظار قرار الإدارة',
  'payroll.openShortDay': 'فتح اليوم الناقص',

  'expenses.title': 'المصروفات',
  'expenses.new': 'مصروف جديد',
  'expenses.categories': 'الفئات',
  'expenses.vendor': 'المورّد',
  'expenses.receipt': 'رقم الإيصال',
  'expenses.markPaid': 'تعليم كمدفوع',
  // Clients (CR-001)
  'clients.title': 'العملاء',
  'clients.new': 'عميل جديد',
  'clients.company': 'الشركة',
  'clients.companyName': 'اسم الشركة',
  'clients.companyNameAr': 'اسم الشركة بالعربية',
  'clients.legalName': 'الاسم القانوني',
  'clients.code': 'كود العميل',
  'clients.codeHint': 'إذا تُرك فارغًا يتم توليد الكود التالي تلقائيًا.',
  'clients.codeFollows': 'آخر كود عميل هو {code}، وهذا الكود الذي يليه. غيّره إن احتجت.',
  'clients.codeFirst': 'هذا أول كود عميل. غيّره إن احتجت.',
  'clients.codeEditHint': 'تعرض الفواتير الكود الحالي للعميل، والقديمة منها أيضًا.',
  'clients.codeTaken': 'كود العميل {code} مستخدم لعميل آخر.',
  'clients.country': 'الدولة',
  'clients.currency': 'عملة التعامل',
  'clients.currencyHint': 'العملة التي نتعامل بها مع هذه الشركة. لا تُستنتج من الدولة.',
  'clients.address': 'العنوان',
  'clients.taxNumber': 'الرقم الضريبي',
  'clients.registrationNumber': 'رقم السجل التجاري',
  'clients.website': 'الموقع الإلكتروني',
  'clients.logo': 'الشعار',
  'clients.logoHint': 'يُطبع أعلى عقود العميل مقابل شعارنا. PNG أو JPG أو WebP أو GIF، ويُحفظ بصيغة PNG.',
  'clients.logoAdd': 'رفع شعار',
  'clients.logoReplace': 'استبدال',
  'clients.logoRemove': 'إزالة',
  'clients.logoUploading': 'جارٍ الرفع…',
  'clients.logoAlt': 'شعار {client}',
  'clients.logoUnreadable': 'تعذّرت قراءة هذه الصورة. استخدم PNG أو JPG أو WebP أو GIF.',
  'clients.logoTooLarge': 'ما زال الشعار أكبر من {mb} ميجابايت بعد تصغيره. استخدم صورة أبسط.',
  'clients.logoRefused': 'تعذّر حفظ الشعار: {reason}',
  'clients.logoStorage': 'تعذّر الوصول إلى مخزن الملفات. حاول مرة أخرى، وإن تكرر ذلك فأبلغ أحد المسؤولين.',
  'clients.contacts': 'جهات الاتصال',
  'clients.newContact': 'إضافة جهة اتصال',
  'clients.jobTitle': 'المسمى الوظيفي',
  'clients.primaryContact': 'جهة الاتصال الرئيسية',
  'clients.contactEnglishNote': 'تُكتب بيانات جهات الاتصال بالإنجليزية فقط: الحروف من A إلى Z والأرقام من 0 إلى 9 ورموز لوحة المفاتيح.',
  'clients.contactEnglishOnly': 'اكتب هذا بالإنجليزية.',
  'clients.contactNotEnglish': 'ليست بالإنجليزية',
  'clients.primary': 'رئيسية',
  'clients.overview': 'نظرة عامة',
  'clients.financialSummary': 'الملخص المالي',
  'clients.totalInvoiced': 'إجمالي الفواتير',
  'clients.totalPaid': 'إجمالي المدفوع',
  'clients.totalOutstanding': 'المتبقي',
  'clients.overdueAmount': 'المتأخر',
  'clients.openInvoices': 'فواتير مفتوحة',
  'clients.collectedLocal': 'المحصّل بالعملة المحلية',
  'clients.deactivate': 'إلغاء التنشيط',
  'clients.reactivate': 'إعادة التنشيط',
  'clients.deactivateConfirm':
    'إلغاء التنشيط يحتفظ بكل المشاريع والفواتير والمدفوعات. العميل فقط يتوقف عن الظهور في القوائم.',
  'clients.inactiveWarning': 'هذا العميل غير نشط ولا يمكن إصدار فواتير له.',
  'clients.noClient': 'بدون عميل — مشروع داخلي',
  'clients.internalProject': 'داخلي',
  'projects.new': 'مشروع جديد',
  'projects.ownOnlyNote': 'تظهر هنا المشاريع التي تملكها أو تنتمي إليها فقط.',
  'projects.addMember': 'إضافة عضو',
  'tickets.rerouteNote':
    'إعادة التوجيه تُسجَّل كحدث توجيه. لقطة المسؤولية الأصلية وموعد الإسناد الأصلي يبقيان كما هما (§11.6).',
  'tickets.reassignNote': 'إعادة الإسناد تُسجَّل في سجل التدقيق، لذا السبب مطلوب.',
  'shifts.snapshotNote': 'تعديل اليوم لا يعيد كتابة لقطة الـSLA الموجودة مسبقًا (§15.5).',
  'shifts.weekdaysHint': 'اترك الكل بدون تحديد لتطبيقه على كل أيام المدى.',
  'shifts.midnightHint': 'النهاية عند البداية أو قبلها تعني أن الوردية تعبر منتصف الليل (§15.3).',
  'shifts.multiplier': 'المضاعف',
  'shifts.multiplierHint': 'مضاعف الأجر عند العمل في هذه العطلة (§18.3).',
  'payroll.adjustmentHint': 'القيمة الموجبة تُضاف إلى الصافي والسالبة تُخصم منه.',
  'qtasks.queueTailNote':
    'المهمة الجديدة تُضاف إلى آخر الطابور، ولا تصبح متاحة إلا بعد إغلاق كل ما قبلها (§21.4).',
  'tasks.generalHint': 'اتركه فارغًا لمهمة عامة.',
  'tasks.projectMemberHint': 'تعرض القائمة أعضاء هذا المشروع فقط.',
  'tickets.slaStartsNote': 'مؤقتات الـSLA تبدأ لحظة إنشاء التذكرة.',
  'violations.cancelHint':
    'الغرامة التي دخلت كشف الرواتب لا يمكن إلغاؤها — سجّل تسوية بدلًا من ذلك (§13.4).',
  'violations.voidHint': 'الإبطال يلغي أيضًا أي غرامة لم تصل إلى كشف الرواتب بعد (§11.6).',
  'projects.clientLocked':
    'هذا المشروع له فواتير بالفعل، لذلك لا يمكن تغيير عميله — الفواتير صدرت على هذا الارتباط.',
  'clients.statement': 'كشف الحساب',
  'clients.noneYet': 'لا يوجد عملاء بعد',
  'clients.noneYetHint': 'كل فاتورة تتبع عميلًا، فابدأ من هنا.',

  // Invoices & payments (CR-001)
  'invoices.client': 'العميل',
  'invoices.project': 'المشروع',
  'invoices.noProject': 'بدون مشروع',
  'invoices.projectHint': 'تظهر مشاريع هذا العميل فقط.',
  'contracts.projectHint': 'تظهر مشاريع هذا العميل فقط، والمنجزة منها أيضًا: حين يبدأ العقد يدخل مشروعه فترة الدعم، ويصبح تاريخ انتهائه اليوم السابق لبدئه.',
  'contracts.projectHintOpen': 'حين يبدأ هذا العقد يدخل {project} فترة الدعم، ويصبح تاريخ انتهائه اليوم السابق لبدء العقد.',
  'contracts.projectHintSupport': '{project} قيد الدعم، ويبقيه هذا العقد كذلك ما دامت تواريخه تشمل اليوم.',
  'contracts.projectHintCompleted': '{project} مكتمل. حين يبدأ هذا العقد يعود قيد الدعم، فتُضاف إليه المهام والتذاكر من جديد، ويصبح تاريخ انتهائه اليوم السابق لبدئه.',
  'contracts.projectHintArchived': '{project} مؤرشف. حفظ هذا العقد يعيده؛ وحين يبدأ العقد يكون قيد الدعم، ويصبح تاريخ انتهائه اليوم السابق لبدئه.',
  'contracts.projectHintCancelled': '{project} ملغى. حفظ هذا العقد يعيده؛ وحين يبدأ العقد يكون قيد الدعم، ويصبح تاريخ انتهائه اليوم السابق لبدئه.',
  'contracts.projectHintSupportByHand':
    '{project} قيد الدعم يدويًا. حين يبدأ هذا العقد يصير دعمه بموجب العقد، ويصبح مكتملًا حين لا يشمل أي عقد اليوم.',
  'invoices.pickClientFirst': 'اختر العميل أولًا',
  'invoices.items': 'بنود الفاتورة',
  'invoices.addItem': 'إضافة بند',
  'invoices.itemDescription': 'الوصف',
  'invoices.quantity': 'الكمية',
  'invoices.unitPrice': 'سعر الوحدة',
  'invoices.lineTotal': 'إجمالي البند',
  'invoices.discount': 'الخصم',
  'invoices.addDiscount': 'إضافة خصم',
  'invoices.changeDiscount': 'تعديل الخصم',
  'invoices.discountIn': 'الخصم بعملة {currency}',
  'invoices.discountHint': '{share} من المجموع الفرعي. بعملة الفاتورة {currency}، وهي عملة دفعاتها أيضًا.',
  'invoices.discountReason': 'السبب',
  'invoices.discountReasonHint': 'اختياري. يُحفظ في سجل التدقيق.',
  'invoices.discountNegative': 'لا يمكن أن يكون الخصم سالبًا.',
  'invoices.discountOverSubtotal': 'لا يمكن أن يتجاوز الخصم المجموع الفرعي، {amount}.',
  'invoices.discountBelowPaid': 'دُفع {paid} بالفعل، لذا لا يمكن أن يتجاوز الخصم {max}.',
  'invoices.discountWholeInvoice': 'لن يبقى شيء للدفع. لإسقاط الفاتورة كلها، ألغِها بدلًا من ذلك.',
  'invoices.discountCancelled': 'لا يمكن إضافة خصم إلى فاتورة ملغاة.',
  'invoices.totalWas': 'كان {amount}',
  'invoices.issue': 'إصدار الفاتورة',
  'invoices.cancel': 'إلغاء الفاتورة',
  'invoices.cancelReason': 'ما سبب إلغاء هذه الفاتورة؟',
  'invoices.collectedLocal': 'المحصّل فعليًا',
  'invoices.owedToday': 'المتبقي',
  'invoices.currencyDiffers': 'هذا العميل تُصدر له الفواتير عادةً بـ {currency}.',
  'invoices.clientCurrency': 'عملة العميل',
  'invoices.currencyOverridden': 'مختلفة',
  'invoices.invoiceCurrency': 'عملة الفاتورة',
  'invoices.rateOnPayment': 'يُدخل مع كل دفعة، بسعر {base} يوم وصول المبلغ.',
  'invoices.ratePerPayment':
    'تحتفظ كل دفعة بالسعر الذي سُجّلت به. أما المتبقي فيُقيَّم بسعر اليوم في الإعدادات › العملات حتى يُدفع.',
  'invoices.noRateToday': 'لا يوجد سعر لعملة {code} في الإعدادات › العملات بعد.',
  'invoices.draftNotice': 'هذه الفاتورة مسودة. أصدرها قبل تسجيل أي دفعة.',
  'invoices.outstandingOnly': 'التي عليها رصيد',
  'invoices.byCurrency': 'حسب العملة',
  'invoices.downloadPdf': 'تنزيل PDF',

  'payments.receipt': 'سند القبض',
  'payments.number': 'رقم الدفعة',
  'payments.exchangeRate': 'سعر الصرف',
  'payments.exchangeRateSource': 'مصدر السعر',
  'payments.localAmount': 'المبلغ بالعملة المحلية',
  'payments.rateHint': 'السعر الساري يوم الدفع. يُحفظ مع الدفعة ولا يُعاد حسابه لاحقًا.',
  'payments.rateOn': 'السعر مقابل {base} بتاريخ {date}',
  'payments.rateOnHint': 'السعر يوم وصول المبلغ. يُحفظ مع هذه الدفعة، فلا يغيّره تعديل السعر لاحقًا.',
  'payments.transferReceipt': 'إيصال التحويل',
  'payments.transferReceiptAdd': 'إرفاق الإيصال',
  'payments.transferReceiptDrop': 'أو أفلت ملف PDF أو صورة هنا',
  'payments.transferReceiptDropHere': 'أفلت الإيصال هنا',
  'payments.transferReceiptUploading': 'جارٍ الرفع…',
  'payments.transferReceiptOpen': 'فتح إيصال التحويل',
  'payments.transferReceiptReplace': 'استبدال',
  'payments.transferReceiptRemove': 'إزالة',
  'payments.transferReceiptHint': 'اختياري. ملف PDF أو صورة (PNG أو JPG أو WebP أو GIF) حتى {mb} ميجابايت.',
  'payments.transferReceiptHintPlain': 'اختياري. ملف PDF أو صورة (PNG أو JPG أو WebP أو GIF).',
  'payments.transferReceiptTooLarge': 'الملف أكبر من {mb} ميجابايت.',
  'payments.transferReceiptWrongType': 'أرفق ملف PDF أو صورة: PNG أو JPG أو WebP أو GIF.',
  'payments.transferReceiptStorage': 'تعذّر الوصول إلى مخزن الملفات. حاول مرة أخرى، وإن تكرر ذلك فأبلغ أحد المسؤولين.',
  'payments.transferReceiptFailed': 'تعذّر رفع الإيصال: {reason}',
  'payments.transferReceiptRefused': 'لم تُحفظ الدفعة، فقد رُفض إيصال التحويل: {reason}',
  'payments.reverse': 'عكس الدفعة',
  'payments.reverseReason': 'ما سبب عكس هذه الدفعة؟',
  'payments.reversed': 'معكوسة',
  'payments.includeReversed': 'إظهار المعكوسة',
  'payments.alreadyPaid': 'المدفوع سابقًا',
  'payments.invoiceTotal': 'إجمالي الفاتورة',
  'payments.collectedTotal': 'المحصّل بالعملة المحلية',

  // Data migration (CR-001 §50–§53)
  'migration.title': 'ترحيل البيانات',
  'migration.subtitle': 'فواتير ومدفوعات سابقة لإعادة الهيكلة المرتكزة على العميل.',
  'migration.complete': 'لم يتبقَّ شيء للترحيل',
  'migration.completeHint': 'كل فاتورة لها عميل وكل دفعة لها فاتورة.',
  'migration.unlinkedInvoices': 'فواتير غير مرتبطة',
  'migration.unlinkedPayments': 'مدفوعات غير مرتبطة',
  'migration.reason': 'سبب وجودها هنا',
  'migration.restore': 'ربط واستعادة',
  'migration.restoreInvoice': 'استعادة الفاتورة',
  'migration.restorePayment': 'استعادة الدفعة',
  'migration.discard': 'استبعاد',
  'migration.discardReason': 'ما سبب استبعاد هذا السجل بدلًا من استعادته؟',
  'migration.originalInvoice': 'الفاتورة الأصلية',
  'migration.pickInvoice': 'أي فاتورة تسدّدها هذه الدفعة؟',
  'migration.restoreFirst': 'استعد فاتورتها أولًا.',
  'migration.noFakeData': 'لا يتم الربط تلقائيًا — عميل وهمي يخفي المشكلة ولا يحلّها.',

  // Finance reports (CR-001 §38)
  'reports.revenue': 'الإيرادات',
  'reports.receivables': 'المستحقات',
  'reports.byClient': 'حسب العميل',
  'reports.byProject': 'حسب المشروع',
  'reports.byCountry': 'حسب الدولة',
  'reports.byCurrency': 'حسب العملة',
  'reports.byPeriod': 'حسب الفترة',
  'reports.aging': 'أعمار الديون',
  'reports.agingCurrent': 'جارية',
  'reports.aging1_30': '1–30 يومًا',
  'reports.aging31_60': '31–60 يومًا',
  'reports.aging61_90': '61–90 يومًا',
  'reports.aging90': 'أكثر من 90 يومًا',
  'reports.outstandingInvoices': 'الفواتير غير المسددة',
  'reports.daysOverdue': 'أيام التأخير',
  'reports.clientStatement': 'كشف حساب العميل',
  'reports.balance': 'الرصيد',
  'reports.debit': 'مدين',
  'reports.credit': 'دائن',
  'reports.collections': 'التحصيلات',
  'reports.collectionsThisMonth': 'المحصّل هذا الشهر',
  'reports.baseCurrency': 'العملة الأساسية',
  'reports.inBaseCurrency': 'كل المبالغ بـ {currency}',
  'reports.billedIn': 'عملة الفوترة',
  'reports.sourceAmount': 'المبلغ الأصلي',
  'reports.rate': 'سعر الصرف',
  'reports.noRateWarning':
    'لا يوجد سعر صرف لـ {currencies}، لذلك استُبعد المتبقي بها من هذه الإجماليات. أضف سعرًا من الإعدادات > العملات.',
  'reports.invoiced': 'إجمالي الفواتير',
  'reports.collected': 'المحصّل',
  'reports.outstanding': 'المتبقي',

  'invoices.title': 'الفواتير',
  'invoices.new': 'فاتورة جديدة',
  'invoices.newForContract': 'فوترة فترة من عقد',
  'invoices.forContract': 'العقد',
  'invoices.contractPeriod': 'الفترة',
  'invoices.suggestedAmount': 'المبلغ المقترح',
  'invoices.contractHint':
    'معبّأة من العقد: العميل والمشروع والعملة هي ما في العقد. راجع الباقي وغيّر ما تحتاج — تُحفظ مسودة تُصدرها من صفحتها.',
  'invoices.fromTheContract': 'من العقد',
  'invoices.counterparty': 'الطرف الآخر',
  'invoices.issueDate': 'تاريخ الإصدار',
  'invoices.dueDate': 'تاريخ الاستحقاق',
  'invoices.subtotal': 'المجموع الفرعي',
  'invoices.tax': 'الضريبة',
  'invoices.paid': 'المدفوع',
  'invoices.remaining': 'المتبقي',
  'invoices.recordPayment': 'تسجيل دفعة',
  'payments.title': 'المدفوعات',
  'payments.new': 'تسجيل دفعة',
  'payments.method': 'طريقة الدفع',
  'payments.reference': 'المرجع',
  'budgets.title': 'الميزانيات',
  'budgets.new': 'ميزانية جديدة',
  'budgets.planned': 'المخطط',
  'budgets.actual': 'الفعلي',
  'budgets.remaining': 'المتبقي',
  'budgets.used': 'مستخدم',
  'budgets.overBudget': 'تجاوز الميزانية',
  'budgets.thresholdHit': 'تم بلوغ حد التنبيه',
  'budgets.scope': 'النطاق',
  'budgets.threshold': 'حد التنبيه',

  'violations.title': 'المخالفات والجزاءات',
  'violations.violations': 'المخالفات',
  'violations.penalties': 'الجزاءات',
  'violations.type': 'النوع',
  'violations.deadline': 'الموعد النهائي',
  'violations.actualAction': 'الإجراء الفعلي',
  'violations.void': 'إلغاء المخالفة',
  'violations.voidReason': 'ما سبب إلغاء هذه المخالفة؟',
  'violations.cancel': 'إلغاء الجزاء',
  'violations.effectiveMonth': 'شهر الراتب',
  'violations.byEmployee': 'حسب الموظف',
  'violations.appliedToPayroll': 'مُطبّق على الراتب',

  'reports.title': 'التقارير',
  'reports.operations': 'العمليات',
  'reports.workforce': 'القوى العاملة',
  'reports.finance': 'المالية',
  'reports.slaCompliance': 'الالتزام بالمهل',
  'reports.onTime': 'في الوقت',
  'reports.late': 'متأخر',
  'reports.avgViewDelay': 'متوسط زمن أول اطلاع',
  'reports.qThroughput': 'إنتاجية مهام الطابور',
  'reports.blockedTime': 'متوسط زمن التعليق',
  'reports.overtimeByEmployee': 'العمل الإضافي لكل موظف',
  'reports.penaltiesByEmployee': 'الجزاءات لكل موظف',

  'settings.title': 'الإعدادات',
  'settings.company': 'الشركة',
  'settings.tickets': 'التذاكر',
  'settings.sla': 'المهل والجزاءات',
  'settings.shifts': 'الورديات',
  'settings.payroll': 'الرواتب',
  'settings.finance': 'المالية',
  'settings.security': 'الأمان',
  'settings.projects': 'المشاريع',
  'settings.jobs': 'المهام الخلفية',
  'settings.currencies': 'العملات',
  'settings.saved': 'تم حفظ الإعدادات.',
  'settings.slaNote':
    'هذه هي القيم الافتراضية للشركة. يمكن للقسم ثم للموظف تجاوزها، بهذا الترتيب.',

  'currencies.title': 'العملات وأسعار الصرف',
  'currencies.note':
    'أوقف العملات التي لا نتعامل بها، فتختفي من كل قوائم اختيار العملة. ' +
    'السعر هنا هو ما يبدأ به سعر الدفعة الجديدة، وبه تُقيَّم الفاتورة غير المدفوعة اليوم: الدفعة تحتفظ ' +
    'بالسعر المحفوظ معها، لذا تعديل السعر هنا لا يغيّر أي مبلغ مُحصَّل من قبل.',
  'currencies.currency': 'العملة',
  'currencies.inUse': 'قيد الاستخدام',
  'currencies.rateTo': 'السعر مقابل {base}',
  'currencies.rateHint': '1 {code} = ؟ {base}',
  'currencies.rateUpdated': 'آخر تحديث للسعر',
  'currencies.base': 'العملة الأساسية',
  'currencies.search': 'ابحث برمز العملة أو اسمها',
  'currencies.inUseCount': '{count} قيد الاستخدام',
  'currencies.missingRate': 'لا يوجد سعر بعد',
  'currencies.saved': 'تم حفظ العملات.',
  'currencies.noneMatch': 'لا توجد عملة تطابق البحث.',
  'currencies.switchAllOff': 'إيقاف الكل',

  'audit.title': 'سجل التدقيق',
  'audit.actor': 'المستخدم',
  'audit.action': 'الإجراء',
  'audit.entity': 'الكيان',
  'audit.occurredAt': 'الوقت',
  'audit.changes': 'التغييرات',

  'notifications.title': 'الإشعارات',
  'notifications.markAllRead': 'تعليم الكل كمقروء',
  'notifications.empty': 'لا توجد إشعارات.',
  'notifications.unread': 'غير مقروء',
  'notifications.sound': 'صوت الإشعارات',
  'notifications.mute': 'كتم صوت الإشعارات',
  'notifications.unmute': 'تشغيل صوت عند وصول إشعار',
  'notifications.noPage': 'لا شيء لفتحه',
  'notifications.desktop': 'إشعارات سطح المكتب',
  'notifications.desktopTurnOn': 'إظهار الإشعارات على سطح المكتب أيضًا',
  'notifications.desktopTurnOff': 'إشعارات سطح المكتب مفعّلة — إيقافها',
  'notifications.desktopBlocked': 'إشعارات سطح المكتب محظورة في هذا المتصفح',
  'notifications.desktopBlockedHint':
    'يحظر هذا المتصفح الإشعارات من هذا الموقع. اسمح بها من إعدادات الموقع — الرمز بجوار العنوان — ثم أعد تحميل الصفحة.',
  'notifications.desktopPageOnly': 'لا توجد في هذا المتصفح خدمة للإشعارات الفورية، لذا تظهر فقط ما دامت صفحة من النظام مفتوحة.',
  'notifications.desktopPromptTitle': 'استقبل الإشعارات على سطح المكتب',
  'notifications.desktopPromptBody':
    'تظهر الإشعارات الجديدة أيضًا في مركز الإشعارات بجهازك — حتى عندما تكون هذه الصفحة في الخلفية أو مغلقة، ما دام المتصفح يعمل.',
  'notifications.desktopPromptOn': 'تفعيل',
  'notifications.desktopPromptLater': 'ليس الآن',
  'notifications.desktopSampleTitle': 'إشعارات سطح المكتب مفعّلة',
  'notifications.desktopSampleBody': 'ستظهر الإشعارات الجديدة هنا ما دامت صفحة من النظام مفتوحة.',

  'systems.admin': 'الإدارة',
  'systems.sys': 'المالي',
  'nav.groupSpending': 'الإنفاق',
  'nav.salaries': 'سجلات الرواتب',
  'nav.sysAccess': 'صلاحيات الدخول',
  'auth.noSysAccess':
    'لا يملك هذا الحساب صلاحية الدخول إلى النظام المالي بعد. اطلب من مدير النظام المالي منحك الصلاحية.',
  'audit.app': 'النظام',
  'audit.sysSubtitle': 'كل تغيير على المالية والرواتب والعملاء وصلاحيات الدخول — من أجراه ومتى ومن أي نظام.',

  'access.title': 'صلاحيات الدخول',
  'access.note':
    'يُنشأ الموظفون في لوحة الإدارة. هنا تمنح الموظف صلاحية الدخول إلى النظام المالي وتحدد ما يمكنه فعله، ' +
    'ولا يُمس حسابه أو أدواره في لوحة الإدارة من هنا.',
  'access.grant': 'منح الصلاحية',
  'access.grantTitle': 'منح موظف صلاحية الدخول إلى النظام المالي',
  'access.editTitle': 'تغيير أدوار النظام المالي',
  'access.revoke': 'سحب الصلاحية',
  'access.revokeTitle': 'سحب صلاحية الدخول إلى النظام المالي',
  'access.revokeNote':
    'تنتهي جلسته في النظام المالي فورًا، ويبقى حسابه في لوحة الإدارة وأدواره هناك وكل ما سجّله كما هو.',
  'access.employee': 'الموظف',
  'access.employeeHint': 'الموظفون النشطون الذين لا يملكون صلاحية بعد. لا تجده؟ أنشئه في لوحة الإدارة أولًا.',
  'access.createInAdmin': 'إنشاء موظف في لوحة الإدارة',
  'access.noCandidates': 'لا يوجد موظف نشط بلا صلاحية يطابق البحث.',
  'access.roles': 'أدوار النظام المالي',
  'access.rolesHint': 'اختر دورًا واحدًا على الأقل. صلاحيات الشخص هي مجموع ما تسمح به أدواره.',
  'access.permissions': '{count} صلاحية',
  'access.since': 'لديه صلاحية منذ',
  'access.lastSignIn': 'آخر دخول إلى النظام المالي',
  'access.disabled': 'معطّل في لوحة الإدارة',
  'access.empty': 'لا أحد يملك صلاحية الدخول إلى النظام المالي بعد.',
  'access.saved': 'تم حفظ الصلاحية.',
  'access.revoked': 'تم سحب الصلاحية.',
  'access.lastAdmin': 'لن يبقى أحد يستطيع منح الصلاحيات. اجعل شخصًا آخر مديرًا للنظام المالي أولًا.',
  'access.role.SYS_ADMIN': 'كل شيء في النظام المالي، بما في ذلك منح الصلاحيات للآخرين.',
  'access.role.ACCOUNTANT': 'العملاء والعقود والفواتير والمدفوعات والمصروفات والميزانيات والرواتب.',
  'access.role.FINANCE_VIEWER': 'يطّلع على العملاء والعقود والفواتير والمدفوعات والتقارير المالية دون أي تعديل.',

  'salaries.title': 'سجلات الرواتب',
  'salaries.note':
    'سجل الراتب الساري اليوم لكل موظف نشط. يُنشأ الموظفون في لوحة الإدارة، وتُحدَّد رواتبهم هنا.',
  'salaries.current': 'الراتب الحالي',
  'salaries.since': 'منذ',
  'salaries.until': 'حتى',
  'salaries.none': 'لا يوجد سجل راتب بعد',
  'salaries.startsLater':
    'يبدأ هذا الراتب في {date}. وحتى ذلك اليوم لا يوجد راتب: لا يُدرج هذا الموظف في الرواتب، ولا قيمة مالية لجزاءاته.',
  'salaries.newRecord': 'سجل راتب جديد',
  'salaries.recordNote':
    'الحفظ يُغلق السجل السابق في اليوم الذي يسبق بدء هذا السجل، فلا يُستبدل شيء استخدمه كشف رواتب سابق.',
  'salaries.dailyHours': 'ساعات العمل اليومية',
  'salaries.dailyHoursHint': 'تُسعّر أجر الساعة في الرواتب. أما الساعات التي يُطالَب بها اليوم فهي «الساعات المعيارية» لورديته، في الورديات بالإدارة.',
  'salaries.monthlyDays': 'أيام العمل الشهرية',
  'salaries.nightAllowancePerShift': 'بدل الوردية الليلية لكل وردية',

  'projectMoney.title': 'أموال المشروع',
  'projectMoney.openInAdmin': 'فتح المشروع في لوحة الإدارة',

  'contracts.title': 'العقود',
  'contracts.subtitle': 'عقود الدعم مع العملاء. تُفوتَر كل فترة يدويًا من صفحة العقد.',
  'contracts.new': 'عقد جديد',
  'contracts.edit': 'تعديل العقد',
  'contracts.number': 'رقم العقد',
  'contracts.contractTitle': 'العنوان',
  'contracts.titleHint': 'يبدأ به نموذج الفاتورة بند كل فترة.',
  'contracts.wording': 'نص العقد',
  'contracts.wordingHint':
    'ما يطبعه ملف PDF للعقد بعد شروطه: التمهيد، ثم البنود بترتيبها، ثم الخاتمة. يمكن أن تتجاور العربية والإنجليزية في النص نفسه.',
  'contracts.preamble': 'التمهيد',
  'contracts.preambleHint': 'يفتتح العقد: من هما الطرفان وما الذي يتفقان عليه.',
  'contracts.clauses': 'البنود',
  'contracts.clausesHint': 'بالعدد الذي يحتاجه العقد، مرقّمة بهذا الترتيب. لكل بند عنوان أو نص أو كلاهما.',
  'contracts.clause': 'البند {n}',
  'contracts.clauseTitle': 'عنوان البند',
  'contracts.clauseText': 'نص البند',
  'contracts.addClause': 'إضافة بند',
  'contracts.removeClause': 'حذف البند {n}',
  'contracts.moveClauseUp': 'نقل البند {n} إلى الأعلى',
  'contracts.moveClauseDown': 'نقل البند {n} إلى الأسفل',
  'contracts.noClauses': 'لا توجد بنود بعد.',
  'contracts.emptyClausesDropped': 'البند الذي ليس له عنوان ولا نص يُحذف عند الحفظ.',
  'contracts.conclusion': 'الخاتمة',
  'contracts.conclusionHint': 'تختم العقد قبل التوقيعات مباشرة.',
  'contracts.signatures': 'التوقيعات',
  'contracts.signaturesHint':
    'في آخر ملف PDF قسم لكل طرف: اسم المسؤول ومنصبه والتاريخ والتوقيع. ما يُكتب هنا من اسم أو منصب يُطبع على سطره، وما يُترك فارغًا يُكتب باليد.',
  'contracts.ourSide': 'الطرف الأول — نحن',
  'contracts.clientSide': 'الطرف الثاني — {client}',
  'contracts.clientSideNone': 'الطرف الثاني — العميل',
  'contracts.personInCharge': 'اسم المسؤول',
  'contracts.positionOfPerson': 'منصب المسؤول',
  'contracts.toFillIn': 'يُكتب على الورق',
  'contracts.nothingWritten': 'لم يُكتب شيء بعد. عدّل العقد لإضافة التمهيد والبنود والخاتمة.',
  'contracts.downloadPdf': 'تنزيل PDF',
  'contracts.downloadPdfHint': 'باللغة التي كُتب بها العقد، مع الشعارين وقسمي التوقيع.',
  'contracts.startDate': 'تاريخ البدء',
  'contracts.endDate': 'تاريخ الانتهاء',
  'contracts.endDateHint': 'آخر يوم يشمله العقد.',
  'contracts.endBeforeStart': 'لا يمكن أن يسبق تاريخ الانتهاء تاريخ البدء.',
  'contracts.runs': 'المدة',
  'contracts.titleAndClient': 'العنوان والعميل',
  'contracts.value': 'قيمة الدفع',
  'contracts.valueBasis': 'القيمة عن',
  'contracts.per.MONTH': 'شهريًا',
  'contracts.per.YEAR': 'سنويًا',
  'contracts.per.TOTAL': 'العقد كاملًا',
  'contracts.basis.MONTH': 'شهر واحد',
  'contracts.basis.YEAR': 'سنة واحدة',
  'contracts.basis.TOTAL': 'العقد كاملًا',
  'contracts.valuePer.MONTH': '{amount} شهريًا',
  'contracts.valuePer.YEAR': '{amount} سنويًا',
  'contracts.valuePer.TOTAL': '{amount} للعقد كاملًا',
  'contracts.billing': 'نوع الدفع',
  'contracts.billing.MONTHLY': 'شهري',
  'contracts.billing.YEARLY': 'سنوي',
  'contracts.billing.ONE_TIME': 'دفعة واحدة',
  'contracts.billingHint.MONTHLY': 'فترة فوترة لكل شهر ميلادي، تُفوتَر كل منها يدويًا. ويُقترح للشهر الجزئي نصيب أيامه.',
  'contracts.billingHint.YEARLY': 'فترة فوترة لكل سنة من سنوات العقد تبدأ في ذكرى تاريخ بدئه، تُفوتَر كل منها يدويًا. ويُقترح للسنة الجزئية نصيب أيامها.',
  'contracts.billingHint.ONE_TIME': 'فترة فوترة واحدة هي العقد كله: فاتورة واحدة تُنشأ يدويًا.',
  'contracts.paymentTerms': 'أيام السداد',
  'contracts.paymentTermsHint': 'يجعل نموذج الفاتورة استحقاق كل فاتورة من فواتيره بعد هذا العدد من الأيام من إصدارها.',
  'contracts.noRate':
    'لا يوجد سعر صرف لعملة {code} في الإعدادات › العملات، فلن تكون للمتبقي على فواتيره قيمة بعملة {base} حتى يُضاف سعر.',
  'contracts.status.ACTIVE': 'سارٍ',
  'contracts.status.UPCOMING': 'لم يبدأ بعد',
  'contracts.status.COMPLETED': 'مكتمل',
  'contracts.status.TERMINATED': 'أُنهي مبكرًا',
  'contracts.toInvoice': 'للفوترة',
  'contracts.dueCount': '{count} للفوترة',
  'contracts.nextPeriod': 'الفترة التالية',
  'contracts.schedule': 'فترات الفوترة',
  'contracts.byHandHint': 'تُفوتَر كل فترة يدويًا: «إنشاء فاتورة» يفتح نموذج الفاتورة معبّأً لها، ولا يُحفظ شيء حتى ترسله.',
  'contracts.createInvoice': 'إنشاء فاتورة',
  'contracts.createInvoiceAhead': 'فوترة مقدّمًا',
  'contracts.cancelledOn': 'مُلغاة: {numbers}',
  'contracts.paidOf': 'دُفع {paid} من {total}',
  'contracts.period': 'الفترة',
  'contracts.state.INVOICED': 'صدرت فاتورتها',
  'contracts.state.DUE': 'لم تُفوتَر',
  'contracts.state.UPCOMING': 'قادمة',
  'contracts.state.SKIPPED': 'متجاوزة',
  'contracts.state.NOT_BILLED': 'لا تُفوتَر — أُنهي العقد مبكرًا',
  'contracts.skippedHint': 'متجاوزة: الفترات التي انقضت قبل إدخال العقد لا تُفوتَر من خلاله، إذ تُعدّ مفوترة من قبل.',
  'contracts.previewTitle': 'فترات الفوترة لهذه الشروط',
  'contracts.previewEmpty': 'أدخل التواريخ وقيمة الدفع لترى فترات فوترته.',
  'contracts.previewInvoices': 'الفترات المطلوب فوترتها',
  'contracts.previewTotal': 'إجمالي ما سيُفوتَر',
  'contracts.issuedAtOnce': 'لا يُفوتَر شيء من تلقاء نفسه: تنتظر كل فترة في صفحة العقد حتى ينشئ أحدهم فاتورتها، من يومها الأول أو مقدّمًا.',
  'contracts.money': 'المبالغ',
  'contracts.summary.total': 'قيمة العقد',
  'contracts.summary.invoiced': 'المُفوتَر',
  'contracts.summary.collected': 'المُحصَّل',
  'contracts.summary.outstanding': 'المتبقي',
  'contracts.summary.notInvoiced': 'لم يُفوتَر بعد',
  'contracts.summary.upcoming': 'فترات قادمة',
  'contracts.summary.otherCurrency': '{count} فاتورة بعملة أخرى خارج هذه الإجماليات.',
  'contracts.payments': 'الدفعات',
  'contracts.paymentsHint': 'المستلمة على فواتيره — لا يُدفع العقد إلا من خلالها.',
  'contracts.noPayments': 'لا دفعات بعد.',
  'contracts.reversed': 'مُلغاة',
  'contracts.newSupport': 'عقد دعم جديد',
  'contracts.noneForProject': 'لا عقد لهذا المشروع بعد. حين يُنجَز، يتيح له عقد دعم يشمل اليوم إضافة المهام والتذاكر.',
  'contracts.terms': 'الشروط',
  'contracts.entered': 'تاريخ الإدخال',
  'contracts.createdBy': 'أنشأه',
  'contracts.fromContract': 'من العقد',
  'contracts.created': 'تم إنشاء العقد.',
  'contracts.saved': 'تم حفظ العقد.',
  'contracts.termsLocked':
    'لهذا العقد فاتورة، فأصبح عميله ومشروعه وتواريخه وقيمة الدفع ونوعه وعملته ثابتة. أنهِه وابدأ عقدًا جديدًا لتغييرها.',
  'contracts.endedLocked': 'انتهى هذا العقد، فلا يتغير منه إلا عنوانه ونصه ومن يوقّعه.',
  'contracts.clientInactive': 'هذا العميل غير نشط، فلا تُفوتَر أي فترة من فترات العقد حتى يُعاد تفعيل العميل.',
  'contracts.terminate': 'إنهاء العقد',
  'contracts.terminateTitle': 'إنهاء هذا العقد مبكرًا',
  'contracts.terminateNote':
    'يتوقف دعمه: لا تُضاف مهام ولا تذاكر جديدة إلى المشروع المنجز الذي يشمله ما لم يشمله عقد آخر. تبقى فواتيره كما هي، وتبقى الفترات التي بدأت حتى اليوم قابلة للفوترة، أما اللاحقة فلا.',
  'contracts.terminateReason': 'لماذا يُنهى العقد مبكرًا؟',
  'contracts.terminatedOn': 'أُنهي مبكرًا في {date} بواسطة {name}.',
  'contracts.ended': 'تم إنهاء العقد.',

  'editor.toolbar': 'التنسيق',
  'editor.bold': 'غامق',
  'editor.italic': 'مائل',
  'editor.underline': 'تسطير',
  'editor.strike': 'يتوسطه خط',
  'editor.heading2': 'عنوان',
  'editor.heading3': 'عنوان فرعي',
  'editor.bulletList': 'قائمة نقطية',
  'editor.orderedList': 'قائمة مرقّمة',
  'editor.quote': 'اقتباس',
  'editor.rule': 'خط فاصل',
  'editor.link': 'رابط',
  'editor.unlink': 'إزالة الرابط',
  'editor.undo': 'تراجع',
  'editor.redo': 'إعادة',
  'editor.linkUrl': 'عنوان الرابط',
  'editor.linkApply': 'تطبيق',
  'editor.linkInvalid': 'أدخل عنوان موقع (https://…) أو بريدًا إلكترونيًا.',

  // مكافآت النقاط — ما استبدل به الموظفون نقاط محافظهم نقدًا (CR-WALLET-001، 2026-10-20).
  'nav.pointsRewards': 'مكافآت النقاط',
  'payroll.pointsReward': 'مكافأة نقاط',
  'payroll.dayReward': 'مكافأة أيام',
  'pointsRewards.title': 'مكافآت النقاط',
  'pointsRewards.subtitle':
    'مبالغ استبدل بها الموظفون نقاط محافظهم. تُصرف كل منها مع الراتب — يحملها أقرب كشف رواتب تحتسبه لشهرها أو ما بعده — أو تُسجَّل مصروفة هنا بعد صرفها بطريقة أخرى.',
  'pointsRewards.tab.PENDING': 'للصرف',
  'pointsRewards.tab.PAID': 'مصروفة',
  'pointsRewards.tab.CANCELLED': 'ملغاة',
  'pointsRewards.tab.all': 'الكل',
  'pointsRewards.status.PENDING': 'للصرف',
  'pointsRewards.status.PAID': 'مصروفة',
  'pointsRewards.status.CANCELLED': 'ملغاة',
  'pointsRewards.pendingTotal': 'للصرف بعملة {currency}',
  'pointsRewards.pendingCount': '{n} مكافأة',
  'pointsRewards.pendingCountOne': 'مكافأة واحدة',
  'pointsRewards.search': 'ابحث بالاسم أو رمز الموظف',
  'pointsRewards.employee': 'الموظف',
  'pointsRewards.redeemed': 'تاريخ الاستبدال',
  'pointsRewards.points': 'النقاط',
  'pointsRewards.bundles': '{n} حزمة',
  'pointsRewards.bundleOne': 'حزمة واحدة',
  'pointsRewards.amount': 'المبلغ',
  'pointsRewards.where': 'طريقة الصرف',
  'pointsRewards.onPayroll': 'على كشف رواتب {month} ({status})',
  'pointsRewards.notOnPayroll': 'في أقرب كشف رواتب يُحتسب، من {month}',
  'pointsRewards.paidWithPayroll': 'مع كشف رواتب {month}',
  'pointsRewards.paidByHand': 'خارج كشف الرواتب',
  'pointsRewards.reference': 'مرجع {ref}',
  'pointsRewards.cancelledBy': 'ألغاها {name}',
  'pointsRewards.markPaid': 'تسجيل الصرف',
  'pointsRewards.cancel': 'إلغاء',
  'pointsRewards.none': 'لا شيء هنا.',
  'pointsRewards.paidTitle': 'تسجيل صرف مكافأة نقاط',
  'pointsRewards.paidHint': 'فقط بعد خروج المبلغ فعلًا — نقدًا أو بتحويل، خارج كشف الرواتب. ثم يراها الموظف مصروفة.',
  'pointsRewards.referenceLabel': 'المرجع',
  'pointsRewards.referenceHint': 'رقم إيصال أو تحويل، إن وُجد.',
  'pointsRewards.paidDone': 'سُجّل الصرف.',
  'pointsRewards.cancelTitle': 'إلغاء مكافأة نقاط',
  'pointsRewards.cancelHint': 'تعود نقاطها إلى محفظة الموظف مرة واحدة، ويُبلَّغ بالإلغاء — لا بسببه.',
  'pointsRewards.cancelOnPayroll': 'إنها على كشف رواتب {month} الذي لم يُعتمد بعد: ستُحذف منه، وينقص صافي تلك القسيمة بمقدار {amount}.',
  'pointsRewards.reasonLabel': 'السبب',
  'pointsRewards.reasonHint': 'يُحفظ في المالية.',
  'pointsRewards.cancelSubmit': 'إلغاء وإعادة {points} نقطة',
  'pointsRewards.cancelDone': 'أُلغيت — وعادت النقاط إلى المحفظة.',
  'pointsRewards.error.REDEMPTION_ALREADY_PROCESSED': 'صُرفت هذه المكافأة أو أُلغيت من قبل، أو هي على كشف رواتب يصرفها.',
  'pointsRewards.error.PAYROLL_PERIOD_LOCKED': 'هذه المكافأة على كشف رواتب معتمد، وتُصرف معه.',
  'pointsRewards.error.CONCURRENT_CHANGE': 'تغيرت هذه المكافأة أو كشف رواتبها في هذه الأثناء. راجعها مجددًا.',
  ...booksAr,
  ...reportsAr,

  // الاشتراكات (2026-10-28)
  'nav.groupSubscriptions': 'الاشتراكات',
  'nav.subscriptionsOverview': 'نظرة عامة',
  'nav.subscriptions': 'الاشتراكات',
  'nav.subscriptionPackages': 'الباقات',
  'nav.subscriptionBilling': 'الفوترة',
  'settings.subscriptions': 'فوترة الاشتراكات',
  'settings.subscriptionsNote':
    'تصدر فاتورة كل اشتراك في أول يوم من فترته عبر التشغيل الدوري كل ساعة، ويحل أجلها بعد أيام السداد. تغيير هذه الإعدادات لا يغيّر أي فاتورة صدرت.',
  'settings.externalSystems': 'الأنظمة الخارجية',
  'subs.title': 'الاشتراكات',
  'subs.subtitle': 'اشتراكات العملاء في باقات المشاريع الداخلية التي نبيعها — تُفوتر كل شهر أو كل سنة حتى تُلغى.',
  'subs.new': 'اشتراك جديد',
  'subs.number': 'الرقم',
  'subs.client': 'العميل',
  'subs.clientAndPackage': 'العميل والباقة',
  'subs.product': 'المنتج',
  'subs.package': 'الباقة',
  'subs.cycle': 'دورة الفوترة',
  'subs.netPrice': 'كل فترة',
  'subs.before': 'قبل الخصم {amount}',
  'subs.nextBilling': 'الفاتورة التالية',
  'subs.overdueCount': '{count} فاتورة متأخرة',
  'subs.system': 'النظام الخارجي',
  'subs.filterEnding': 'ينتهي بنهاية فترته',
  'subs.filterFailing': 'فشلت الفوترة',
  'subs.billingBy': 'الفاتورة التالية حتى',
  'subs.startedFrom': 'بدأ من',
  'subs.exportCsv': 'تصدير CSV',
  'subs.fromSubscription': 'من الاشتراك',
  'subs.noneForProject': 'لا يوجد اشتراك في هذا المنتج بعد.',
  'subs.projectCard': 'يُباع بالاشتراك',
  'subs.projectCardOffered': 'معروض بالاشتراك — {count} باقة معروضة للبيع.',
  'subs.status.ACTIVE': 'نشط',
  'subs.status.PAST_DUE': 'متأخر السداد',
  'subs.status.CANCELLED': 'ملغى',
  'subs.status.UPCOMING': 'يبدأ {date}',
  'subs.status.ENDING': 'ينتهي {date}',
  'subs.cycle.MONTHLY': 'شهري',
  'subs.cycle.YEARLY': 'سنوي',
  'subs.per.MONTHLY': 'شهريًا',
  'subs.per.YEARLY': 'سنويًا',
  'subs.discount.NONE': 'بلا خصم',
  'subs.discount.PERCENT': 'نسبة مئوية',
  'subs.discount.FIXED': 'مبلغ ثابت',
  'subs.source.FINANCE': 'المالية',
  'subs.source.API': 'نظام خارجي (واجهة برمجية)',
  'subs.created': 'تم إنشاء الاشتراك.',
  'subs.createdInvoiced': 'تم إنشاء الاشتراك — وصدرت الفاتورة {number}.',
  'subs.overview.title': 'نظرة عامة على الاشتراكات',
  'subs.overview.subtitle': 'الإيراد المتكرر المتعاقد عليه، وما فُوتر، وما حُصّل فعلًا — ثلاثة أرقام مختلفة، كلٌّ بعملته.',
  'subs.kpi.active': 'نشطة',
  'subs.kpi.pastDue': 'متأخرة السداد',
  'subs.kpi.ending': 'على وشك الانتهاء',
  'subs.kpi.endingHint': 'تنتهي بنهاية فترتها',
  'subs.kpi.cancelled': 'ملغاة',
  'subs.kpi.mrr': 'الإيراد الشهري المتكرر',
  'subs.kpi.arr': 'الإيراد السنوي المتكرر',
  'subs.kpi.mrrHint': 'من الأسعار الصافية للاتفاقات السارية، والسنوية تُحسب بجزء من اثني عشر شهريًا — متعاقد عليه، لا نقدًا.',
  'subs.kpi.inBase': 'بـ {currency} بأسعار اليوم — متعاقد عليه، لا نقدًا',
  'subs.kpi.unconverted': 'لا سعر اليوم لـ {list}: خارج هذا المجموع',
  'subs.recurringByCurrency': 'الإيراد المتكرر حسب العملة',
  'subs.live': 'سارية',
  'subs.billingRange': 'المفوتر والمحصّل، {from} – {to}',
  'subs.collectedBase': 'النقد المحصّل: {amount}، بسعر كل دفعة',
  'subs.invoiced': 'المفوتر',
  'subs.collected': 'المحصّل',
  'subs.outstanding': 'المستحق',
  'subs.invoicesCount': '{count} فاتورة',
  'subs.paymentsCount': '{count} دفعة',
  'subs.byProduct': 'حسب المنتج',
  'subs.byPackage': 'حسب الباقة',
  'subs.aging': 'المستحق حسب أيام التأخير',
  'subs.agingHint': 'فواتير الاشتراكات التي ما زال عليها مبالغ.',
  'subs.aging.current': 'لم يحل أجلها',
  'subs.aging.d1_30': '1–30 يومًا',
  'subs.aging.d31_60': '31–60 يومًا',
  'subs.aging.d61_90': '61–90 يومًا',
  'subs.aging.d90_plus': 'أكثر من 90 يومًا',
  'subs.nothingOwed': 'لا يوجد مستحق.',
  'subs.trend': 'المبدوءة والملغاة حسب الشهر',
  'subs.started': 'بدأت',
  'subs.cancelledCount': 'أُلغيت',
  'subs.upcoming': 'ما يُفوتر في الثلاثين يومًا القادمة',
  'subs.nothingUpcoming': 'لا شيء يُفوتر في الثلاثين يومًا القادمة.',
  'subs.billing.title': 'فوترة الاشتراكات',
  'subs.billing.subtitle': 'يصدر التشغيل الدوري كل ساعة فاتورة كل فترة في أول يوم منها. لا يُفوتر شيء قبل يومه، ولا تُفوتر فترة مرتين.',
  'subs.billing.run': 'شغّل الفوترة الآن',
  'subs.billing.ran': 'تمت الفوترة: {invoices} فاتورة، و{failures} حالة فشل.',
  'subs.billing.busy': 'هناك تشغيل للفوترة جارٍ الآن.',
  'subs.billing.autoOff': 'الفوترة التلقائية متوقفة في الإعدادات › فوترة الاشتراكات: لا يُفوتر شيء حتى تُعاد، أو يدويًا.',
  'subs.billing.due': 'مستحق الآن',
  'subs.billing.dueHint': 'فترات بدأت ولم تُفوتر بعد — فارغة عادةً بين التشغيلات.',
  'subs.billing.failing': 'فشلت الفوترة',
  'subs.billing.failingHint': 'تبقى الفترة مستحقة وتُعاد محاولتها كل ساعة بعد زوال السبب.',
  'subs.billing.upcoming': 'خلال الأيام الأربعة عشر القادمة',
  'subs.billing.pastDue': 'متأخرة السداد',
  'subs.billing.pastDueHint': 'فاتورة غير مدفوعة بعد {days} يومًا من تاريخ استحقاقها. لا يُلغى شيء بسببها، وتستمر الفوترة.',
  'subs.billing.runs': 'آخر التشغيلات',
  'subs.billing.trigger.SCHEDULE': 'كل ساعة',
  'subs.billing.trigger.MANUAL': 'يدويًا',
  'subs.billing.startedAt': 'البداية',
  'subs.billing.examined': 'فُحص',
  'subs.billing.invoices': 'الفواتير',
  'subs.billing.failures': 'الإخفاقات',
  'subs.billing.none': 'لا شيء هنا.',
  'subs.detail.billNow': 'فوتر الآن',
  'subs.detail.billed': 'فُوتر: {list}.',
  'subs.detail.nothingDue': 'لا شيء مستحق: تبدأ الفترة التالية في {date}.',
  'subs.detail.changeTerms': 'تغيير الاتفاق',
  'subs.detail.cancel': 'إلغاء الاشتراك',
  'subs.detail.resume': 'أبقِه ساريًا',
  'subs.detail.resumed': 'يستمر الاشتراك.',
  'subs.detail.billingError': 'فشلت آخر محاولة فوترة ({date}): {error}',
  'subs.detail.pastDue': 'متأخر السداد منذ {date}: فاتورة غير مدفوعة بعد تاريخ استحقاقها وأيام المهلة. تستمر الفوترة.',
  'subs.detail.ending': 'ينتهي في {date}، بنهاية فترته المفوترة. لا يُفوتر ما بعده.',
  'subs.detail.cancelled': 'أُلغي في {date}{by}.',
  'subs.detail.invoices': 'الفواتير',
  'subs.detail.invoicesHint': 'فاتورة كل فترة، تصدر في أول يوم منها. إنها فواتير عادية: تُدفع أو يُخصم منها أو تُلغى من صفحتها.',
  'subs.detail.noInvoices': 'لم يُفوتر شيء بعد.',
  'subs.detail.free': 'لا شيء للدفع: لا تُفوتر فتراته.',
  'subs.detail.period': 'الفترة',
  'subs.detail.remaining': 'المتبقي',
  'subs.detail.due': 'تستحق {date}',
  'subs.detail.reportedBy': 'أبلغ عنها {name} — العملية {transaction}',
  'subs.detail.receipt': 'الإيصال',
  'subs.detail.reinvoice': 'فوترها مجددًا',
  'subs.detail.reinvoiced': 'فُوترت مجددًا.',
  'subs.detail.versions': 'الاتفاق',
  'subs.detail.versionsHint': 'كل نسخة، وأول فترة تسري عليها.',
  'subs.detail.versionShort': '#',
  'subs.detail.applies': 'يسري',
  'subs.detail.effectiveFrom': 'من {date}',
  'subs.detail.history': 'السجل',
  'subs.detail.agreement': 'الاتفاق',
  'subs.detail.listPrice': 'سعر القائمة',
  'subs.detail.basePrice': 'السعر المتفق عليه',
  'subs.detail.discount': 'الخصم',
  'subs.detail.net': 'كل فترة',
  'subs.detail.startDate': 'يبدأ',
  'subs.detail.currentPeriod': 'الفترة المفوترة',
  'subs.detail.nextPeriod': 'الفترة التالية',
  'subs.detail.nextPeriodValue': '{start} – {end}، تُفوتر في أول يوم منها',
  'subs.detail.endsOn': 'ينتهي في',
  'subs.detail.source': 'أُنشئ في',
  'subs.detail.createdBy': 'أنشأه',
  'subs.detail.money': 'المبالغ',
  'subs.history.subscription.created': 'أُنشئ',
  'subs.history.subscription.updated': 'عُدّل',
  'subs.history.subscription.terms_changed': 'تغيّر الاتفاق',
  'subs.history.subscription.cancelled': 'أُلغي',
  'subs.history.subscription.cancellation_scheduled': 'ضُبط لينتهي بنهاية فترته',
  'subs.history.subscription.resumed': 'أُبقي ساريًا',
  'subs.history.subscription.past_due': 'متأخر السداد',
  'subs.history.subscription.back_in_good_standing': 'عاد منتظمًا',
  'subs.history.subscription.billing_failed': 'فشلت الفوترة',
  'subs.history.system': 'نظام خارجي',
  'subs.history.automatic': 'تلقائي',
  'subs.form.chooseClient': 'اختر عميلًا',
  'subs.form.clientHint': 'شركة عميلة نشطة.',
  'subs.form.chooseProduct': 'اختر منتجًا',
  'subs.form.productHint': 'مشروع داخلي معروض بالاشتراك.',
  'subs.form.choosePackage': 'اختر باقة',
  'subs.form.notSold': 'غير مبيع',
  'subs.form.agreedPrice': 'سعر كل فترة',
  'subs.form.priceHint': 'سعر القائمة للباقة: {price}.',
  'subs.form.customPrice': 'سعر خاص لهذا العميل',
  'subs.form.ownPrice': 'خاص',
  'subs.form.discountPercent': 'الخصم (%)',
  'subs.form.discountAmount': 'الخصم ({currency})',
  'subs.form.startDate': 'تاريخ البدء',
  'subs.form.startHint': 'أول يوم من فترته الأولى. إن كان قد مضى: تُفوتر الفترة التي يقع فيها اليوم فقط.',
  'subs.form.system': 'مرتبط بنظام خارجي',
  'subs.form.systemHint': 'يقرؤه ذلك النظام حينها مع فواتيره وإيصالاته، وقد يبلغ عن مدفوعاته.',
  'subs.form.externalRef': 'مرجعهم',
  'subs.form.externalRefHint': 'مرجع النظام الخارجي الخاص به.',
  'subs.form.summary': 'ما يبلغه',
  'subs.form.choosePackageFirst': 'اختر باقة لترى ما يبلغه.',
  'subs.form.firstPeriod': 'الفترة الأولى',
  'subs.form.invoicedNow': 'تصدر فاتورته الأولى فور حفظه.',
  'subs.form.invoicedLater': 'تصدر فاتورته الأولى في {date}.',
  'subs.form.currencyNote': 'يُفوتر العميل بـ {client}؛ وسعر هذه الباقة بـ {package}.',
  'subs.form.currencyRefused': 'الفوترة بعملة أخرى تتطلب صلاحية finance.exchange_rate.edit.',
  'subs.form.noTax': 'لا تُضاف ضريبة: السعر هو ما يُفوتر.',
  'subs.form.save': 'إنشاء الاشتراك',
  'subs.terms.title': 'تغيير اتفاق {number}',
  'subs.terms.hint': 'يسري من الفترة التالية غير المفوترة، التي تبدأ في {date}. ما فُوتر يبقى كما هو.',
  'subs.terms.firstPeriod': 'أول فترة بالشروط الجديدة',
  'subs.terms.packageHint': 'باقة أخرى من المنتج نفسه وبالعملة نفسها.',
  'subs.terms.cycleRestarts': 'الدورة الجديدة تعدّ فتراتها من {date}.',
  'subs.terms.systemFixed': 'أنشأه نظام خارجي، فيبقى مرتبطًا به.',
  'subs.terms.reason': 'السبب',
  'subs.terms.reasonHint': 'يُحفظ مع النسخة الجديدة وفي سجل التدقيق.',
  'subs.terms.save': 'حفظ الاتفاق الجديد',
  'subs.terms.saved': 'تم حفظ الاتفاق.',
  'subs.cancel.title': 'إلغاء {number}',
  'subs.cancel.periodEnd': 'بنهاية فترته',
  'subs.cancel.periodEndHint': 'يستمر حتى {date}، آخر يوم مفوتر، ثم ينتهي.',
  'subs.cancel.periodEndNone': 'لا توجد فترة مفوترة يستمر حتى نهايتها، فينتهي اليوم.',
  'subs.cancel.now': 'الآن',
  'subs.cancel.nowHint': 'ينتهي اليوم. لا يُفوتر شيء بعده.',
  'subs.cancel.invoicesStay': 'تبقى الفواتير والمدفوعات كما هي. الفاتورة التي لا ينبغي دفعها تُلغى أو يُمنح لها خصم من صفحتها.',
  'subs.cancel.reasonHint': 'يُحفظ في الاشتراك وفي سجل التدقيق.',
  'subs.cancel.submit': 'إلغاء الاشتراك',
  'subs.cancel.done': 'تم إلغاء الاشتراك.',
  'subs.cancel.scheduled': 'ينتهي في {date}.',
  'subs.cancel.openInvoices': 'ما زالت مفتوحة: {list}.',
  'pkgs.title': 'الباقات',
  'pkgs.subtitle': 'المشاريع الداخلية التي نبيعها بالاشتراك وباقاتها. سعر القائمة مجرد نقطة بداية لكل اشتراك جديد.',
  'pkgs.products': 'المشاريع المعروضة',
  'pkgs.productsHint': 'مشاريع داخلية اختيرت لتُباع بالاشتراك.',
  'pkgs.noProjects': 'لا يوجد مشروع داخلي بعد.',
  'pkgs.noProjectsHint': 'المشروع الداخلي مشروع بلا عميل. تُنشأ المشاريع في لوحة الإدارة.',
  'pkgs.noneOffered': 'لا يوجد مشروع معروض بعد.',
  'pkgs.noneOfferedHint': 'لا تظهر هنا إلا المشاريع الداخلية المختارة للبيع بالاشتراك.',
  'pkgs.offerProject': 'اعرض مشروعًا',
  'pkgs.offerProjectHint': 'اختر مشروعًا داخليًا لتبيعه بالاشتراك. ينضم إلى القائمة في صفحة الباقات، وهناك تضيف باقاته.',
  'pkgs.project': 'المشروع الداخلي',
  'pkgs.projectHint': 'مشروع بلا عميل يُنشأ في لوحة الإدارة. لا تظهر هنا المشاريع المعروضة بالفعل ولا المؤرشفة ولا الملغاة.',
  'pkgs.pickProject': 'اختر مشروعًا',
  'pkgs.nothingToOffer': 'كل المشاريع الداخلية معروضة بالفعل أو مؤرشفة أو ملغاة. تُنشأ المشاريع الداخلية في لوحة الإدارة.',
  'pkgs.notOffered': 'غير معروض',
  'pkgs.offeredHint': 'يُباع بالاشتراك.',
  'pkgs.notOfferedHint': 'لا يُباع بالاشتراك. اعرضه لتضيف له باقات.',
  'pkgs.offer': 'اعرضه بالاشتراك',
  'pkgs.stopOffering': 'أوقف عرضه',
  'pkgs.offeredDone': 'صار معروضًا بالاشتراك.',
  'pkgs.stoppedDone': 'لم يعد معروضًا بالاشتراك.',
  'pkgs.stillRunning': 'فيه {count} اشتراك ساري: ألغِها أولًا.',
  'pkgs.running': '{count} ساري',
  'pkgs.chooseProject': 'اختر مشروعًا داخليًا.',
  'pkgs.showArchived': 'إظهار الباقات المؤرشفة',
  'pkgs.none': 'لا توجد باقة بعد.',
  'pkgs.noneHint': 'أضف له باقات أساسية واحترافية ومؤسسية — لكلٍّ سعر شهري أو سنوي أو كلاهما.',
  'pkgs.new': 'باقة جديدة',
  'pkgs.code': 'الرمز',
  'pkgs.codeHint': 'ما تسميها به الأنظمة الخارجية: أحرف لاتينية كبيرة وأرقام و- و_. يثبت بعد أول اشتراك.',
  'pkgs.name': 'الاسم',
  'pkgs.nameAr': 'الاسم بالعربية',
  'pkgs.description': 'الوصف',
  'pkgs.currency': 'العملة',
  'pkgs.currencyHint': 'يُفوتر بها مشتركوها. تثبت بعد أول اشتراك.',
  'pkgs.monthlyPrice': 'السعر الشهري',
  'pkgs.yearlyPrice': 'السعر السنوي',
  'pkgs.priceHint': 'اتركه فارغًا إن لم تُبع بهذه الطريقة.',
  'pkgs.listPrices': 'أسعار القائمة',
  'pkgs.listPriceNote': 'سعر قائمة: تغييره لا يغيّر أي اشتراك أو فاتورة أو دفعة.',
  'pkgs.notSold': 'غير مبيع',
  'pkgs.onSale': 'معروضة للبيع',
  'pkgs.archived': 'مؤرشفة',
  'pkgs.archive': 'أرشفة',
  'pkgs.restore': 'أعدها للبيع',
  'pkgs.archiveTitle': 'أرشفة {name}',
  'pkgs.archiveHint': 'لن يستطيع أي عميل جديد الاشتراك فيها. تستمر اشتراكاتها السارية ({count}) بما اتُّفق عليه.',
  'pkgs.archiveHintNone': 'لن يستطيع أي عميل جديد الاشتراك فيها. لا أحد مشترك فيها الآن.',
  'pkgs.archivedDone': 'تمت أرشفة الباقة.',
  'pkgs.restored': 'عادت الباقة للبيع.',
  'pkgs.saved': 'تم حفظ الباقة.',
  'pkgs.created': 'تم إنشاء الباقة.',
  'pkgs.editTitle': 'تعديل {name}',
  'pkgs.subscribers': 'الاشتراكات السارية',
  'pkgs.noSubscribers': 'لا أحد مشترك فيها الآن.',
  'pkgs.everSubscribed': 'إجمالي الاشتراكات',
  'extsys.title': 'الأنظمة الخارجية',
  'extsys.subtitle':
    'أنظمة خارج فيجانيوم — كبوابة عملاء لأحد المنتجات — تقرأ الاشتراكات والفواتير وتبلغ عن المدفوعات التي تتلقاها، ولكلٍّ منها رموز وصول خاصة.',
  'extsys.new': 'تسجيل نظام',
  'extsys.none': 'لا يوجد نظام خارجي بعد.',
  'extsys.noneHint': 'سجّل نظامًا، وامنحه مشاريع وعملاء، ثم أصدر له رمزًا.',
  'extsys.name': 'الاسم',
  'extsys.code': 'الرمز',
  'extsys.codeHint': 'أحرف لاتينية صغيرة وأرقام وشرطات. يُشتق من الاسم إن تُرك فارغًا.',
  'extsys.codeFixed': 'يثبت بعد التسجيل.',
  'extsys.description': 'الوصف',
  'extsys.editTitle': 'تعديل {name}',
  'extsys.saved': 'تم حفظ النظام الخارجي.',
  'extsys.created': 'تم تسجيل النظام الخارجي.',
  'extsys.status.active': 'نشط',
  'extsys.status.suspended': 'موقوف',
  'extsys.failedEvents': 'فشل إرسال {count} إشعار',
  'extsys.reach': 'نطاق الوصول',
  'extsys.reachValue': '{projects} مشروع · {clients} عميل · {subscriptions} اشتراك',
  'extsys.lastUsed': 'آخر استخدام',
  'extsys.neverUsed': 'لم يُستخدم قط',
  'extsys.access': 'ما يصل إليه',
  'extsys.scopes': 'الصلاحيات',
  'extsys.scopesHint': 'أقصى ما يمكن لرموزه فعله. سحب صلاحية يسحبها من كل رموزه فورًا.',
  'extsys.projects': 'المشاريع الداخلية',
  'extsys.projectsHint': 'لا يرى شيئًا من أي مشروع آخر.',
  'extsys.noOfferedProjects': 'لا يوجد مشروع داخلي معروض بالاشتراك بعد.',
  'extsys.clients': 'العملاء الذين يمثلهم',
  'extsys.clientsHint': 'ينشئ اشتراكات لهؤلاء العملاء فقط. والاشتراكات التي تربطها المالية به تظهر له أيضًا.',
  'extsys.customPricing': 'يحدد أسعاره وخصوماته بنفسه',
  'extsys.customPricingHint': 'إن كان متوقفًا: تأخذ اشتراكاته سعر القائمة للباقة دون خصم.',
  'extsys.cidrs': 'العناوين المسموح بها',
  'extsys.cidrsHint': 'عنوان أو نطاق CIDR في كل سطر؛ الفراغ يعني أي مكان. حماية إضافية فقط — الرمز هو ما يسمح له بالدخول.',
  'extsys.anywhere': 'أي مكان',
  'extsys.subscriptionsLinked': 'الاشتراكات المرتبطة',
  'extsys.reportedPayments': 'المدفوعات المبلغ عنها',
  'extsys.tokens': 'رموز الوصول',
  'extsys.tokensHint': 'يُعرض كل رمز مرة واحدة عند إصداره. لا يحتفظ فيجانيوم إلا ببصمته.',
  'extsys.noTokens': 'لا يوجد رمز بعد.',
  'extsys.tokenName': 'الاسم',
  'extsys.tokenNameHint': 'مكان استخدامه: «التكامل الإنتاجي».',
  'extsys.tokenScopes': 'ما يمكنه فعله',
  'extsys.tokenScopesHint': 'بعض صلاحيات النظام — لا أكثر مما يحتاج.',
  'extsys.noScopes': 'لا يملك النظام أي صلاحية: امنحه بعضها أولًا.',
  'extsys.tokenExpires': 'ينتهي في',
  'extsys.tokenExpiresHint': 'اختياري، وبحد أقصى سنتين. إن تُرك فارغًا لا ينتهي.',
  'extsys.tokenIssue': 'إصدار الرمز',
  'extsys.issueToken': 'إصدار رمز',
  'extsys.tokenIssuedTitle': 'صدر الرمز «{name}»',
  'extsys.token': 'الرمز',
  'extsys.tokenOnce': 'انسخه الآن واحفظه في مكان آمن. يُعرض هذه المرة فقط: لا يحتفظ فيجانيوم إلا ببصمته ولا يستطيع عرضه مجددًا.',
  'extsys.copy': 'نسخ',
  'extsys.copied': 'تم النسخ.',
  'extsys.copyFailed': 'تعذّر النسخ: حدّده وانسخه يدويًا.',
  'extsys.savedAck': 'حفظته في مكان آمن',
  'extsys.done': 'تم',
  'extsys.tokenStatus.ACTIVE': 'يعمل',
  'extsys.tokenStatus.EXPIRED': 'منتهي',
  'extsys.tokenStatus.REVOKED': 'ملغى',
  'extsys.until': 'حتى {date}',
  'extsys.failedAttempts': '{count} محاولة فاشلة',
  'extsys.rotate': 'تدوير',
  'extsys.rotateTitle': 'تدوير «{name}»',
  'extsys.rotateHint':
    'يحل محله رمز جديد بالاسم والصلاحيات وتاريخ الانتهاء نفسها. يبقى القديم يعمل للساعات المختارة — وقتًا كافيًا لوضع الجديد مكانه — ثم يتوقف.',
  'extsys.overlap': 'ساعات بقاء الرمز القديم',
  'extsys.overlapNone': 'لا شيء: يتوقف فورًا',
  'extsys.overlapHours': '{hours} ساعة',
  'extsys.rotateSubmit': 'تدوير الرمز',
  'extsys.rotatedTitle': 'تم تدوير «{name}»',
  'extsys.replacedUntil': 'يعمل الرمز القديم حتى {date}.',
  'extsys.revoke': 'إلغاء',
  'extsys.revokeTitle': 'إلغاء «{name}»',
  'extsys.revokeHint': 'يتوقف عن العمل فورًا ونهائيًا. يبقى سجله.',
  'extsys.revoked': 'تم إلغاء الرمز.',
  'extsys.suspend': 'إيقاف',
  'extsys.suspendTitle': 'إيقاف {name}',
  'extsys.suspendHint': 'تُرفض كل طلباته من الآن. تبقى رموزه وسجله لحين عودته.',
  'extsys.suspended': 'تم إيقاف النظام الخارجي.',
  'extsys.suspendedSince': 'موقوف منذ {date}: {reason}',
  'extsys.reactivate': 'إعادة تفعيل',
  'extsys.reactivated': 'تمت إعادة تفعيل النظام الخارجي.',
  'extsys.webhook': 'الإشعارات (Webhook)',
  'extsys.webhookHint': 'ما يُبلَّغ به فور حدوثه، موقّعًا. ويمكنه دائمًا قراءة المعلومات نفسها عبر الواجهة البرمجية.',
  'extsys.webhookUrl': 'العنوان',
  'extsys.webhookUrlHint': 'HTTPS. يُرسل كل حدث إليه بطلب POST بصيغة JSON موقّعًا بالترويسة X-Viganium-Signature.',
  'extsys.webhookEvents': 'الأحداث',
  'extsys.webhookEventsHint': 'إن لم يُحدد شيء: كل الأحداث.',
  'extsys.everyEvent': 'كل الأحداث',
  'extsys.webhooksEnabled': 'إرسال الإشعارات',
  'extsys.secretFirst': 'أصدر سر التوقيع أولًا من صفحة النظام.',
  'extsys.webhookSecret': 'سر التوقيع',
  'extsys.secretSet': 'محدد',
  'extsys.secretNotSet': 'غير محدد',
  'extsys.newSecret': 'سر توقيع جديد',
  'extsys.secretNew': 'السر الذي يتحقق به النظام من توقيع كل إرسال. يُعرض مرة واحدة.',
  'extsys.secretReplaces': 'السر الجديد يحل محل الحالي فورًا: على النظام أخذ الجديد وإلا رفض ما يُرسل إليه.',
  'extsys.secretOnce': 'انسخ سر التوقيع الآن: يُعرض هذه المرة فقط. يتحقق به النظام من الترويسة X-Viganium-Signature في كل إرسال.',
  'extsys.testWebhook': 'إرسال تجريبي',
  'extsys.testSent': 'الإرسال التجريبي في طريقه.',
  'extsys.events': 'عمليات الإرسال',
  'extsys.eventsHint': 'تُرسل مرة واحدة على الأقل، ويُعاد الإرسال على فترات متزايدة بعد الفشل.',
  'extsys.noEvents': 'لم يُرسل شيء بعد.',
  'extsys.event': 'الحدث',
  'extsys.event.PENDING': 'بالانتظار',
  'extsys.event.DELIVERED': 'وصل',
  'extsys.event.FAILED': 'فشل',
  'extsys.attempts': 'المحاولات',
  'extsys.nextAttempt': 'المحاولة التالية {date}',
  'extsys.redeliver': 'أعد الإرسال',
  'extsys.redelivered': 'سيُعاد إرساله.',
  'extsys.history': 'السجل',
  'extsys.history.external_system.created': 'سُجّل',
  'extsys.history.external_system.updated': 'عُدّل',
  'extsys.history.external_system.suspended': 'أُوقف',
  'extsys.history.external_system.reactivated': 'أُعيد تفعيله',
  'extsys.history.external_system.token_issued': 'صدر رمز',
  'extsys.history.external_system.token_revoked': 'أُلغي رمز',
  'extsys.history.external_system.token_rotated': 'دُوّر رمز',
  'extsys.history.external_system.webhook_secret_issued': 'صدر سر توقيع',
  'extsys.history.external_system.webhook_tested': 'أُرسل إرسال تجريبي',
  'extsys.history.external_system.event_redelivered': 'أُعيد إرسال حدث',
  'extsys.apiBase': 'عنوان الواجهة البرمجية',
  'extsys.apiBaseHint': 'يرسل النظام الرمز في الترويسة Authorization: Bearer، عبر HTTPS فقط.',
  'extsys.scope.packages:read': 'قراءة الباقات',
  'extsys.scope.subscriptions:read': 'قراءة الاشتراكات',
  'extsys.scope.subscriptions:create': 'إنشاء اشتراكات',
  'extsys.scope.subscriptions:update': 'تعديل الاشتراكات',
  'extsys.scope.subscriptions:cancel': 'إلغاء الاشتراكات',
  'extsys.scope.invoices:read': 'قراءة الفواتير',
  'extsys.scope.payments:read': 'قراءة المدفوعات',
  'extsys.scope.payments:report': 'الإبلاغ عن المدفوعات',
  'extsys.scope.receipts:read': 'جلب الإيصالات',
};

const DICTIONARIES: Record<Locale, Record<TranslationKey, string>> = { en, ar };

interface I18nContextValue {
  locale: Locale;
  dir: 'ltr' | 'rtl';
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, vars?: Record<string, string | number>) => string;
  /** Picks the Arabic field when the UI is Arabic and one exists. */
  localized: (row: { name?: string | null; nameAr?: string | null; fullName?: string; fullNameAr?: string | null } | null | undefined) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const LOCALE_KEY = 'viganium.locale';

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(
    () => (localStorage.getItem(LOCALE_KEY) as Locale | null) ?? 'en',
  );

  const dir: 'ltr' | 'rtl' = locale === 'ar' ? 'rtl' : 'ltr';

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
    localStorage.setItem(LOCALE_KEY, locale);
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => setLocaleState(next), []);

  const t = useCallback(
    (key: TranslationKey, vars?: Record<string, string | number>) => {
      let text: string = DICTIONARIES[locale][key] ?? en[key] ?? key;
      if (vars) for (const [k, v] of Object.entries(vars)) text = text.replace(`{${k}}`, String(v));
      return text;
    },
    [locale],
  );

  const localized = useCallback<I18nContextValue['localized']>(
    (row) => {
      if (!row) return '—';
      if (locale === 'ar') return row.nameAr || row.fullNameAr || row.name || row.fullName || '—';
      return row.name || row.fullName || '—';
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, dir, setLocale, t, localized }), [locale, dir, setLocale, t, localized]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used inside <I18nProvider>.');
  return ctx;
}

/** Enum labels come from the API as SCREAMING_SNAKE; render them readably in both languages. */
const ENUM_AR: Record<string, string> = {
  // Ticket status
  NEW: 'جديدة', ASSIGNED: 'مُسندة', IN_PROGRESS: 'قيد التنفيذ', WAITING_REQUESTER: 'بانتظار مقدّم الطلب',
  RESOLVED: 'تم الحل', CLOSED: 'مغلقة', REOPENED: 'أُعيد فتحها', CANCELLED: 'ملغاة',
  // Task status
  TO_DO: 'قيد الانتظار', BLOCKED: 'معلّقة', REVIEW: 'مراجعة', DONE: 'منجزة',
  // Q Task
  QUEUED: 'في الطابور', AVAILABLE: 'متاحة', SKIPPED: 'متخطاة',
  // Priority
  LOW: 'منخفضة', MEDIUM: 'متوسطة', HIGH: 'عالية', URGENT: 'عاجلة',
  // Shifts
  MORNING: 'صباحي', NIGHT: 'ليلي', CUSTOM: 'مخصص', OFF: 'إجازة',
  // Project
  DRAFT: 'مسودة', ACTIVE: 'نشط', TESTING: 'قيد الاختبار', ON_HOLD: 'متوقف', SUPPORT: 'قيد الدعم', COMPLETED: 'مكتمل', ARCHIVED: 'مؤرشف',
  // Payroll / finance
  CALCULATED: 'محتسب', APPROVED: 'معتمد', PAID: 'مدفوع', LOCKED: 'مقفل',
  PENDING: 'قيد الانتظار', APPLIED_TO_PAYROLL: 'مطبّق على الراتب', SLA_PENALTY: 'جزاء',
  ISSUED: 'صادرة', PARTIALLY_PAID: 'مدفوعة جزئيًا', OVERDUE: 'متأخرة',
  RECEIVABLE: 'مستحقة لنا', PAYABLE: 'مستحقة علينا', INCOMING: 'واردة', OUTGOING: 'صادرة',
  // Violations
  VIEW_SLA_MISSED: 'تجاوز مهلة الاطلاع',
  ASSIGNMENT_SLA_MISSED: 'تجاوز مهلة الإسناد',
  DEPARTMENT_VIEW_SLA_BREACH: 'تجاوز مهلة الاطلاع على مستوى القسم',
  OPEN: 'مفتوحة', VOIDED: 'ملغاة',
  // Work logs
  SUBMITTED: 'مُرسل', REJECTED: 'مرفوض',
  // Member type
  MEMBER: 'عضو', LEADER: 'قائد',
  // CR-001 — client status and the source of a payment's exchange rate (§28).
  INACTIVE: 'غير نشط',
  MANUAL: 'يدوي', BANK: 'بنك', EXCHANGE_OFFICE: 'مكتب صرافة', OTHER: 'أخرى',
  // A budget's scope (DEPARTMENT only on budgets kept from before 2026-10-29).
  COMPANY: 'الشركة', EXPENSE_CATEGORY: 'فئة مصروفات', DEPARTMENT: 'القسم',
};

/**
 * English labels the Title Case rule would get wrong. A payroll SLA_PENALTY line
 * carries every approved penalty — a break that ran past its limit as well as a
 * missed SLA — so it reads as a penalty, not "Sla Penalty".
 */
const ENUM_EN: Record<string, string> = { SLA_PENALTY: 'Penalty' };

export function enumLabel(value: string | null | undefined, locale: Locale): string {
  if (!value) return '—';
  if (locale === 'ar' && ENUM_AR[value]) return ENUM_AR[value];
  if (locale === 'en' && ENUM_EN[value]) return ENUM_EN[value];
  return value
    .split('_')
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

/** «مشروع» is masculine, where the shared labels are an invoice's, a ticket's or a task's («ملغاة»). */
const PROJECT_STATUS_AR: Record<string, string> = { CANCELLED: 'ملغى' };

/** A project's status, in the reader's language. */
export function projectStatusLabel(value: string | null | undefined, locale: Locale): string {
  if (value && locale === 'ar' && PROJECT_STATUS_AR[value]) return PROJECT_STATUS_AR[value];
  return enumLabel(value, locale);
}
